import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import ControlsHeader from "shared/ControlsHeader";
import SearchBar from "shared/SearchBar";
import Button, { ButtonPurpose } from "shared/Button";
import { FilterColumn } from "shared/FilterColumn";
import Pagination from "shared/Pagination";
import FormActionBar from "shared/@forms/FormActionBar";
import PublishCard from "shared/@forms/PublishCard";
import { Modal } from "shared/Modal";
import Loader from "shared/Loader";
import HistoryOverlay from "DreamCast/Shared/HistoryOverlay";
import SortModal from "DreamCast/Shared/SortModal";
import AudioListItem from "DreamCast/Shared/AudioListItem";
import AudioDetailsForm from "DreamCast/Shared/AudioDetailsForm";
import ThumbnailPreview from "DreamCast/Shared/ThumbnailPreview";
import ThumbnailBuilder from "DreamCast/Shared/ThumbnailBuilder";
import FileUploadSection from "DreamCast/Shared/FileUploadSection";
import ContentSettingsCard from "DreamCast/Shared/ContentSettingsCard";
import { THUMBNAIL_LAYOUT } from "DreamCast/Shared/ThumbnailBuilder/thumbnailLayout";
import { svgDataUrlToPngFile } from "DreamCast/Shared/ThumbnailBuilder/svgToPng";
import {
  buildTagOptionsByTypeCode,
  extractSettingsTagDefinitions,
} from "DreamCast/Shared/ContentSettingsCard/tagSchema";
import AssignSpeakersSection from "DreamCast/Shared/AssignSpeakersSection";
import { matchesCategoryFilter } from "DreamCast/Shared/categoryFilter";
import MediaDisclosuresSection, {
  findIncompatibleDisclosures,
  withDisclosureAssignmentIds,
} from "DreamCast/Shared/MediaDisclosuresSection";
import {
  createDreamcastMedia,
  fetchDreamcastContentSettingsConfig,
  duplicateDreamcastMedia,
  fetchDreamcastMediaById,
  fetchDreamcastMediaFilesById,
  fetchDreamcastMedia,
  fetchDreamcastDisclosures,
  setDreamcastMediaArchiveState,
  updateDreamcastMedia,
  uploadDreamcastThumbnailFile,
  moveMediaTempArchivedFiles,
  moveMediaTempImage,
  deleteMediaFiles,
  moveMediaActiveFile,
  restoreMediaArchivedFile,
} from "DreamCast/api/media";
import { runSaveFileOperations } from "./saveFileOperations";
import formatDate from "shared/@utils/formatDate";
import { getMediaTypeGroup } from "./mediaType";
import styles from "./Media.module.css";

const SORT_OPTIONS = [
  { label: "Date: Newest", value: "date-newest" },
  { label: "Date: Oldest", value: "date-oldest" },
  { label: "Audio: A to Z", value: "audio-asc" },
  { label: "Audio: Z to A", value: "audio-desc" },
];

const FILTER_SECTIONS = [
  {
    label: "Type",
    options: [
      { label: "WWG", value: "wwg" },
      { label: "3rd Party", value: "3rd" },
    ],
  },
  {
    label: "Category",
    options: [
      { label: "Audio", value: "Audio" },
      { label: "Video", value: "Video" },
      { label: "Music", value: "Music" },
    ],
  },
  {
    label: "Language",
    options: [],
  },
  {
    label: "Tag",
    options: [],
  },
  {
    label: "Status",
    options: [
      { label: "Published", value: "published" },
      { label: "Scheduled", value: "upcoming" },
      { label: "In Review", value: "in-review" },
      { label: "Archived", value: "archived" },
    ],
  },
];

const DEFAULT_PERMISSION_STATE = {
  membershipPermissions: { complimentary: true, premier: true },
  pinPermissions: { ibo: true, platinum: true, emerald: true, diamond: true },
  membershipOptions: [],
  pinLevels: [],
  membershipGroupValuesById: { premier: 1, complimentary: 2 },
  familyReunionGroups: [],
  selectedFamilyGroups: [],
  diamonds: [],
  selectedDiamonds: [],
  additionalVisibilitySections: [],
  visibilitySelectionsByTypeCode: {},
  additionalIbos: "",
  tags: {
    locationOptions: [],
    locations: [],
    selectedLocations: [],
    languageOptions: [],
    languages: [],
    selectedLanguages: [],
    occupationOptions: [],
    occupations: [],
    selectedOccupations: [],
    keywordOptions: [],
    keywords: [],
    hiddenTagOptions: [],
    hiddenTags: [],
    tagDefinitions: [],
    byTypeCode: {},
  },
};

const DEFAULT_AUDIO_DETAILS = {
  mediaType: "Audio",
  sourceCode: "",
  contentType: "Event",
  descriptionType: "Description",
  descriptionValue: "",
  year: "",
  releaseOn: "",
  sku: "",
  title: "",
  creatorSpeaker: "",
  topic: "",
  naCrNumber: "",
  anzCrNumber: "",
  isThirdParty: false,
};

const withoutMarkAsNewFields = (details = {}) => {
  const sanitized = { ...details };
  delete sanitized.isNew;
  delete sanitized.markAsNewUntil;
  return sanitized;
};

const DEFAULT_MEDIA_SOURCE_OPTIONS = [
  {
    label: "WWG Exclusive",
    value: "MEDIA_SOURCE_WWG_EXCLUSIVE",
    isThirdParty: false,
  },
  {
    label: "3rd Party",
    value: "MEDIA_SOURCE_3RD_PARTY",
    isThirdParty: true,
  },
];

const inferIsThirdParty = (label, value) => {
  const normalized = `${String(label || "")} ${String(
    value || "",
  )}`.toLowerCase();
  return normalized.includes("3rd") || normalized.includes("third party");
};

const normalizeMediaSourceOptions = (attributes = []) => {
  const options = attributes
    .map((entry) => {
      const label = String(
        entry?.Attribute || entry?.label || entry?.name || "",
      ).trim();
      if (!label) {
        return null;
      }

      const value = String(
        entry?.AttributeCode || entry?.value || entry?.code || label,
      ).trim();

      return {
        label,
        value,
        isThirdParty: inferIsThirdParty(label, value),
      };
    })
    .filter(Boolean);

  return options.length > 0 ? options : DEFAULT_MEDIA_SOURCE_OPTIONS;
};

const extractMediaSourceOptionsFromConfig = (config) => {
  if (!config) {
    return DEFAULT_MEDIA_SOURCE_OPTIONS;
  }

  if (
    Array.isArray(config.mediaSourceOptions) &&
    config.mediaSourceOptions.length > 0
  ) {
    return normalizeMediaSourceOptions(config.mediaSourceOptions);
  }

  const candidateGroups = [
    config.attributeTypes,
    config.attributes,
    config.contentAttributes,
    config.configContentSettings,
  ];

  const flattened = candidateGroups.filter(Array.isArray).flat();

  const mediaSourceDefinition = flattened.find((entry) => {
    const section = String(entry?.Section || "").toUpperCase();
    const typeCode = String(entry?.AttributeTypeCode || "").toUpperCase();
    const typeName = String(entry?.AttributeType || "").toUpperCase();
    const componentType = String(entry?.ComponentTypeCode || "").toUpperCase();

    if (section && section !== "MEDIA") {
      return false;
    }

    if (typeCode !== "MEDIA_SOURCE") {
      return false;
    }

    if (!componentType) {
      return true;
    }

    return componentType === "PRIMARY_CHECKBOX";
  });

  return normalizeMediaSourceOptions(
    mediaSourceDefinition?.Attributes ||
      mediaSourceDefinition?.attributes ||
      [],
  );
};

const DEFAULT_MEDIA_TYPE_OPTIONS = ["Audio", "Video", "Music"];
const DEFAULT_MEDIA_TEMPLATES = [
  {
    label: "Event",
    titleLabel: "Title",
    subtitleLabel: "Speaker",
    captionLabel: "Event",
  },
  {
    label: "Bio",
    titleLabel: "Title",
    subtitleLabel: "Speaker",
    captionLabel: "Occupation",
  },
  {
    label: "Music",
    titleLabel: "Title",
    subtitleLabel: "Artist",
    captionLabel: "Genre",
  },
];

const extractMediaTypeOptionsFromConfig = (config) => {
  const options = Array.isArray(config?.mediaTypes)
    ? config.mediaTypes
        .map((type) => String(type?.label || type?.MediaType || "").trim())
        .filter(Boolean)
    : [];

  return options.length > 0 ? options : DEFAULT_MEDIA_TYPE_OPTIONS;
};

const extractMediaTemplatesFromConfig = (config) => {
  const templates = Array.isArray(config?.mediaTemplates)
    ? config.mediaTemplates
        .map((template) => ({
          label: String(template?.label || "").trim(),
          titleLabel: String(template?.titleLabel || "").trim(),
          subtitleLabel: String(template?.subtitleLabel || "").trim(),
          captionLabel: String(template?.captionLabel || "").trim(),
        }))
        .filter(
          (template) =>
            template.label &&
            template.titleLabel &&
            template.subtitleLabel &&
            template.captionLabel,
        )
    : [];

  return templates.length > 0 ? templates : DEFAULT_MEDIA_TEMPLATES;
};

const createDefaultPublishState = (digitalDeliveryEnabled = false) => ({
  status: "Published",
  publishOn: "",
  unpublishOn: "",
  unpublishEnabled: false,
  digitalDeliveryEnabled,
  showAsUpcomingOn: "",
  showAsCurrentUntil: "",
});

const placeMediaCopiesBeforeSources = (items) => {
  const copiesBySource = new Map();
  const itemIds = new Set(items.map((item) => String(item.mediaItemId)));

  items.forEach((item) => {
    const sourceId = item.duplicateOf;
    if (sourceId != null && itemIds.has(String(sourceId))) {
      const copies = copiesBySource.get(String(sourceId)) || [];
      copies.push(item);
      copiesBySource.set(String(sourceId), copies);
    }
  });

  const appendWithCopies = (ordered, item, visited = new Set()) => {
    const itemId = String(item.mediaItemId);
    if (visited.has(itemId)) {
      return ordered;
    }

    const nextVisited = new Set(visited).add(itemId);
    const copies = copiesBySource.get(String(item.mediaItemId)) || [];
    const withCopies = copies.reduce(
      (result, copy) => appendWithCopies(result, copy, nextVisited),
      ordered,
    );
    return [...withCopies, item];
  };

  return items.reduce((ordered, item) => {
    if (item.duplicateOf != null && itemIds.has(String(item.duplicateOf))) {
      return ordered;
    }

    return appendWithCopies(ordered, item);
  }, []);
};

// Uploads land in a temp folder; saving moves them to the item's folder, so a
// temp URL stops resolving once the save's file operations finish.
const isTempUploadUrl = (url) =>
  /\/(medias|profiles|playlists)\/temp\//.test(String(url || ""));

const buildOptimisticMediaListItem = (currentItem, payload, mediaItemId) => {
  const publishState = payload.publishState || {};
  const normalizedStatus = String(publishState.status || "")
    .toLowerCase()
    .replace(/\s+/g, "-");
  const publishTimestamp = Date.parse(publishState.publishOn || "");
  const isPastScheduledItem =
    ["scheduled", "upcoming"].includes(normalizedStatus) &&
    Number.isFinite(publishTimestamp) &&
    publishTimestamp <= Date.now();
  const workflowStatus = isPastScheduledItem
    ? "published"
    : normalizedStatus === "scheduled"
    ? "upcoming"
    : normalizedStatus || currentItem?.workflowStatus || "in-review";
  const displayStatus = {
    published: "Available",
    upcoming: "Scheduled",
    archived: "Archived",
    "in-review": "In Review",
  }[workflowStatus];

  return {
    ...currentItem,
    mediaItemId,
    title: payload.detailState?.title || "Untitled Audio",
    contributorName: payload.detailState?.creatorSpeaker || "",
    sku: currentItem?.sku || payload.detailState?.sku || "",
    mediaTypeCode: String(
      payload.detailState?.mediaType || currentItem?.mediaTypeCode || "audio",
    ).toLowerCase(),
    date:
      publishState.publishOn || currentItem?.date || new Date().toISOString(),
    PublishOn: publishState.publishOn || currentItem?.PublishOn || null,
    workflowStatus,
    displayStatus,
    isDraft: Boolean(payload.isDraft),
    thumbnailPath: payload.thumbnail || currentItem?.thumbnailPath || null,
    publishState: {
      ...currentItem?.publishState,
      ...publishState,
      status: workflowStatus,
    },
  };
};

// ---- Undo / History (ported from the WWG Store product editor) ---------------
// Field definitions for the HistoryOverlay: each entry extracts its value from
// a form snapshot (dotted key) and formats it for display.
const formatHistoryDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
      })
    : "";

const formatYesNo = (v) => (v ? "Yes" : "No");

const formatList = (v) => (Array.isArray(v) ? v.join(", ") : "");

const MEDIA_HISTORY_FIELD_DEFS = [
  { key: "details.title", label: "Title", format: (v) => v ?? "" },
  {
    key: "details.creatorSpeaker",
    label: "Creator/Speaker",
    format: (v) => v ?? "",
  },
  { key: "details.topic", label: "Topic", format: (v) => v ?? "" },
  { key: "details.year", label: "Year", format: (v) => v ?? "" },
  { key: "details.sku", label: "SKU", format: (v) => v ?? "" },
  { key: "details.mediaType", label: "Media Type", format: (v) => v ?? "" },
  { key: "details.sourceCode", label: "Source", format: (v) => v ?? "" },
  {
    key: "details.contentType",
    label: "Content Type",
    format: (v) => v ?? "",
  },
  {
    key: "details.descriptionType",
    label: "Description Type",
    format: (v) => v ?? "",
  },
  {
    key: "details.descriptionValue",
    label: "Description",
    format: (v) => v ?? "",
  },
  { key: "details.naCrNumber", label: "NA CR Number", format: (v) => v ?? "" },
  {
    key: "details.anzCrNumber",
    label: "ANZ CR Number",
    format: (v) => v ?? "",
  },
  { key: "details.isThirdParty", label: "3rd Party", format: formatYesNo },
  {
    key: "thumbnail",
    label: "Thumbnail Image",
    format: (v) =>
      v?.src ? v.src.split("/").pop().split("?")[0].slice(0, 60) : "",
  },
  {
    key: "files",
    label: "Files",
    format: (v) =>
      Array.isArray(v) ? `${v.length} file${v.length !== 1 ? "s" : ""}` : "",
  },
  {
    key: "archivedFiles",
    label: "Archived Files",
    format: (v) =>
      Array.isArray(v) ? `${v.length} file${v.length !== 1 ? "s" : ""}` : "",
  },
  { key: "publishState.status", label: "Status", format: (v) => v ?? "" },
  {
    key: "publishState.publishOn",
    label: "Publish On",
    format: formatHistoryDate,
  },
  {
    key: "publishState.unpublishOn",
    label: "Unpublish On",
    format: formatHistoryDate,
  },
  {
    key: "publishState.unpublishEnabled",
    label: "Unpublish on this date",
    format: (v) => (v ? "True" : "False"),
  },
  {
    key: "publishState.digitalDeliveryEnabled",
    label: "Digital Delivery",
    format: formatYesNo,
  },
  {
    key: "publishState.showAsUpcomingOn",
    label: "Show as Upcoming On",
    format: formatHistoryDate,
  },
  {
    key: "publishState.showAsCurrentUntil",
    label: "Show as Current Until",
    format: formatHistoryDate,
  },
  {
    key: "contentSettings.membershipPermissions",
    label: "Membership Permissions",
    format: (v) =>
      v
        ? Object.entries(v)
            .filter(([, on]) => on)
            .map(([k]) => k.charAt(0).toUpperCase() + k.slice(1))
            .join("\n")
        : "",
  },
  {
    key: "contentSettings.pinPermissions",
    label: "Pin Level Permissions",
    format: (v) =>
      v
        ? Object.entries(v)
            .filter(([, on]) => on)
            .map(([k]) => k.charAt(0).toUpperCase() + k.slice(1))
            .join("\n")
        : "",
  },
  {
    key: "contentSettings.selectedFamilyGroups",
    label: "Family Reunions",
    format: (v) =>
      Array.isArray(v)
        ? v.map((g) => g?.name || g?.label || String(g)).join("\n")
        : "",
  },
  {
    key: "contentSettings.selectedDiamonds",
    label: "Diamonds",
    format: (v) =>
      Array.isArray(v)
        ? v.map((g) => g?.name || g?.label || String(g)).join("\n")
        : "",
  },
  {
    key: "contentSettings.visibilitySelectionsByTypeCode",
    label: "Additional Visibility",
    format: (v) =>
      v
        ? Object.entries(v)
            .map(
              ([code, ids]) =>
                `${code}: ${Array.isArray(ids) ? ids.length : 0} selected`,
            )
            .join("\n")
        : "",
  },
  {
    key: "contentSettings.additionalIbos",
    label: "Additional IBOs",
    format: (v) => v ?? "",
  },
  {
    key: "contentSettings.tags.selectedLocations",
    label: "Locations",
    format: formatList,
  },
  {
    key: "contentSettings.tags.selectedLanguages",
    label: "Languages",
    format: formatList,
  },
  {
    key: "contentSettings.tags.selectedOccupations",
    label: "Occupations",
    format: formatList,
  },
  {
    key: "contentSettings.tags.keywords",
    label: "Tags",
    format: formatList,
  },
  {
    key: "contentSettings.tags.hiddenTags",
    label: "Hidden Tags",
    format: formatList,
  },
  {
    key: "speakers",
    label: "Speakers",
    format: (v) =>
      Array.isArray(v) ? v.map((s) => s?.name || s?.id).join("\n") : "",
  },
  {
    key: "disclosures",
    label: "Disclosures",
    format: (v) =>
      Array.isArray(v)
        ? v
            .map((item) => {
              const positions = [
                item.playAtStart ? "Start" : "",
                item.playAtEnd ? "End" : "",
              ].filter(Boolean);
              return `${item.title || "Select"} (${positions.join(" & ")})`;
            })
            .join("\n")
        : "",
  },
];

// Stable stringify (sorted keys) so object compares ignore key order.
const sortObjectKeys = (value) => {
  if (Array.isArray(value)) return value.map(sortObjectKeys);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortObjectKeys(value[key]);
        return acc;
      }, {});
  }
  return value;
};

// Project a file list to a stable identity string. Signed preview URLs carry
// rotating tokens, so compare by name/size/version instead.
const fileListKey = (files) =>
  (Array.isArray(files) ? files : [])
    .map((f) => `${f?.name || ""}|${f?.size || ""}|${f?.version || ""}`)
    .join("||");

// Immutable set of a dotted path inside a plain-object tree.
const setNestedValue = (obj, path, value) => {
  const [head, ...rest] = path.split(".");
  if (rest.length === 0) {
    return { ...obj, [head]: value };
  }
  return {
    ...obj,
    [head]: setNestedValue(obj?.[head] || {}, rest.join("."), value),
  };
};

const escapeSvgText = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Escape attribute values for SVG (replace double quotes with &quot;)
const escapeAttribute = (value) => String(value || "").replace(/"/g, "&quot;");

// Not currently used, but kept for reference.
// const buildThumbnailDataUrl = (builderData) => {
//   try {
//     if (!builderData) {
//       console.error("buildThumbnailDataUrl: builderData is null/undefined");
//       return null;
//     }

//     // Use pre-escaped, pre-wrapped text from ThumbnailBuilder
//     const creatorLines = builderData.renderedText?.creatorLines || ["N/A"];
//     const titleLines = builderData.renderedText?.titleLines || ["N/A"];
//     const topicLines = builderData.renderedText?.topicLines || ["N/A"];
//     const finalSku = builderData.renderedText?.sku || "N/A";
//     const finalCr = builderData.renderedText?.cr || "";

//     const code = escapeSvgText(builderData.selected || "");
//     const year = escapeSvgText(builderData.year || "");

//     // Use the previewBackground exactly as displayed in ThumbnailBuilder
//     const previewBg = builderData.previewBackground || "#243746";

//     // Detect if it's a CSS gradient or solid color
//     const isGradientCSS = previewBg.startsWith("linear-gradient");

//     let backgroundDef = "";

//     if (isGradientCSS) {
//       // Parse CSS gradient: linear-gradient(40deg, #FFFFFF, #243746)
//       const gradientMatch = previewBg.match(
//         /linear-gradient\((\d+)deg,\s*(.+?)\)/,
//       );
//       if (gradientMatch) {
//         const angle = Number(gradientMatch[1]) || 40;
//         const colorString = gradientMatch[2];
//         const gradientColors = colorString
//           .split(",")
//           .map((c) => c.trim())
//           .filter((c) => c && c.startsWith("#"))
//           .slice(0, 3);

//         // Convert CSS angle to SVG gradient endpoints
//         const radians = (angle * Math.PI) / 180;
//         const dx = Math.sin(radians);
//         const dy = -Math.cos(radians);
//         const x1 = 50 - dx * 50;
//         const y1 = 50 - dy * 50;
//         const x2 = 50 + dx * 50;
//         const y2 = 50 + dy * 50;

//         if (gradientColors.length >= 2) {
//           backgroundDef = `<defs><linearGradient id="bg" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">${gradientColors
//             .map(
//               (color, index) =>
//                 `<stop offset="${Math.round(
//                   (index / Math.max(gradientColors.length - 1, 1)) * 100,
//                 )}%" stop-color="${color}" />`,
//             )
//             .join("")}</linearGradient></defs><rect width="${
//             THUMBNAIL_LAYOUT.width
//           }" height="${THUMBNAIL_LAYOUT.height}" fill="url(#bg)"/>`;
//         } else {
//           backgroundDef = `<rect width="${THUMBNAIL_LAYOUT.width}" height="${THUMBNAIL_LAYOUT.height}" fill="${previewBg}"/>`;
//         }
//       } else {
//         backgroundDef = `<rect width="${THUMBNAIL_LAYOUT.width}" height="${THUMBNAIL_LAYOUT.height}" fill="${previewBg}"/>`;
//       }
//     } else {
//       // Solid color - use directly
//       backgroundDef = `<rect width="${THUMBNAIL_LAYOUT.width}" height="${THUMBNAIL_LAYOUT.height}" fill="${previewBg}"/>`;
//     }

//     // Determine text color based on background
//     const useDarkText = builderData.useDarkPreviewText || false;
//     const textColor = useDarkText ? "#243746" : "#ffffff";

//     // Icon badge positioning
//     // Badge is at right: 8px, width: 30px, so center is at: 160 - 8 - 15 = 137px
//     const badgeX =
//       THUMBNAIL_LAYOUT.width -
//       THUMBNAIL_LAYOUT.badge.right -
//       THUMBNAIL_LAYOUT.badge.width / 2;
//     const badgeY =
//       THUMBNAIL_LAYOUT.badge.top + THUMBNAIL_LAYOUT.badge.height / 2;

//     // Render creator text with multiple lines
//     // First line starts at: textContainer.top + paddingTop
//     // Subsequent lines add: lineHeight + gap for each line
//     let creatorY =
//       THUMBNAIL_LAYOUT.textContainer.top + THUMBNAIL_LAYOUT.primary.paddingTop;
//     const creatorTexts = creatorLines
//       .map((line, idx) => {
//         const y =
//           creatorY +
//           idx *
//             (THUMBNAIL_LAYOUT.primary.lineHeight +
//               THUMBNAIL_LAYOUT.primary.gap);
//         return `<text x="${
//           THUMBNAIL_LAYOUT.textContainer.left
//         }" y="${y}" font-family="${escapeAttribute(
//           THUMBNAIL_LAYOUT.primary.fontFamily,
//         )}" font-size="${THUMBNAIL_LAYOUT.primary.fontSize}" font-weight="${
//           THUMBNAIL_LAYOUT.primary.fontWeight
//         }" fill="${textColor}">${line}</text>`;
//       })
//       .join("\n  ");

//     // Render title text
//     // Title starts after all creator lines
//     const titleY =
//       creatorY +
//       creatorLines.length *
//         (THUMBNAIL_LAYOUT.primary.lineHeight + THUMBNAIL_LAYOUT.primary.gap);
//     const titleTexts = titleLines
//       .map((line, idx) => {
//         const y =
//           titleY +
//           idx *
//             (THUMBNAIL_LAYOUT.title.lineHeight + THUMBNAIL_LAYOUT.title.gap);
//         return `<text x="${
//           THUMBNAIL_LAYOUT.textContainer.left
//         }" y="${y}" font-family="${escapeAttribute(
//           THUMBNAIL_LAYOUT.title.fontFamily,
//         )}" font-size="${THUMBNAIL_LAYOUT.title.fontSize}" font-weight="${
//           THUMBNAIL_LAYOUT.title.fontWeight
//         }" fill="${textColor}" opacity="${
//           THUMBNAIL_LAYOUT.title.opacity
//         }">${line}</text>`;
//       })
//       .join("\n  ");

//     // Render topic text
//     // Topic starts after all title lines
//     const topicY =
//       titleY +
//       titleLines.length *
//         (THUMBNAIL_LAYOUT.secondary.lineHeight +
//           THUMBNAIL_LAYOUT.secondary.gap);
//     const topicTexts = topicLines
//       .map((line, idx) => {
//         const y =
//           topicY +
//           idx *
//             (THUMBNAIL_LAYOUT.secondary.lineHeight +
//               THUMBNAIL_LAYOUT.secondary.gap);
//         const secondaryOpacity = useDarkText
//           ? "1"
//           : THUMBNAIL_LAYOUT.secondary.opacity;
//         return `<text x="${
//           THUMBNAIL_LAYOUT.textContainer.left
//         }" y="${y}" font-family="${escapeAttribute(
//           THUMBNAIL_LAYOUT.secondary.fontFamily,
//         )}" font-size="${THUMBNAIL_LAYOUT.secondary.fontSize}" font-weight="${
//           THUMBNAIL_LAYOUT.secondary.fontWeight
//         }" fill="${textColor}" opacity="${secondaryOpacity}">${line}</text>`;
//       })
//       .join("\n  ");

//     // Meta section positioning
//     const metaY =
//       THUMBNAIL_LAYOUT.height -
//       THUMBNAIL_LAYOUT.meta.bottom -
//       THUMBNAIL_LAYOUT.sku.lineHeight;
//     const skuY = metaY;
//     const crY =
//       metaY + THUMBNAIL_LAYOUT.sku.lineHeight + THUMBNAIL_LAYOUT.meta.gap;

//     const svg = `
// <svg xmlns="http://www.w3.org/2000/svg" width="${
//       THUMBNAIL_LAYOUT.width
//     }" height="${THUMBNAIL_LAYOUT.height}" viewBox="0 0 ${
//       THUMBNAIL_LAYOUT.width
//     } ${THUMBNAIL_LAYOUT.height}">
//   ${backgroundDef}
//   <rect x="${
//     THUMBNAIL_LAYOUT.width -
//     THUMBNAIL_LAYOUT.badge.right -
//     THUMBNAIL_LAYOUT.badge.width
//   }" y="${THUMBNAIL_LAYOUT.badge.top}" width="${
//       THUMBNAIL_LAYOUT.badge.width
//     }" height="${THUMBNAIL_LAYOUT.badge.height}" fill="none" stroke="${
//       textColor === "#ffffff" ? "rgba(255,255,255,0.7)" : "rgba(36,55,70,0.65)"
//     }" stroke-width="${THUMBNAIL_LAYOUT.badge.borderWidth}" />
//   <text x="${badgeX}" y="${
//       badgeY - 1
//     }" text-anchor="middle" font-family="${escapeAttribute(
//       THUMBNAIL_LAYOUT.badge.code.fontFamily ||
//         THUMBNAIL_LAYOUT.primary.fontFamily,
//     )}" font-size="${THUMBNAIL_LAYOUT.badge.code.fontSize}" font-weight="${
//       THUMBNAIL_LAYOUT.badge.code.fontWeight
//     }" fill="${textColor}">${code}</text>
//   <text x="${badgeX}" y="${
//       badgeY + 8
//     }" text-anchor="middle" font-family="${escapeAttribute(
//       THUMBNAIL_LAYOUT.badge.year.fontFamily ||
//         THUMBNAIL_LAYOUT.primary.fontFamily,
//     )}" font-size="${THUMBNAIL_LAYOUT.badge.year.fontSize}" font-weight="${
//       THUMBNAIL_LAYOUT.badge.year.fontWeight
//     }" letter-spacing="${
//       THUMBNAIL_LAYOUT.badge.year.letterSpacing
//     }" fill="${textColor}">${year}</text>

//   ${creatorTexts}
//   ${titleTexts}
//   ${topicTexts}

//   <text x="${
//     THUMBNAIL_LAYOUT.meta.left
//   }" y="${skuY}" font-family="${escapeAttribute(
//       THUMBNAIL_LAYOUT.sku.fontFamily,
//     )}" font-size="${THUMBNAIL_LAYOUT.sku.fontSize}" font-weight="${
//       THUMBNAIL_LAYOUT.sku.fontWeight
//     }" fill="${textColor}">${finalSku}</text>
//   <text x="${
//     THUMBNAIL_LAYOUT.meta.left
//   }" y="${crY}" font-family="${escapeAttribute(
//       THUMBNAIL_LAYOUT.cr.fontFamily,
//     )}" font-size="${THUMBNAIL_LAYOUT.cr.fontSize}" font-weight="${
//       THUMBNAIL_LAYOUT.cr.fontWeight
//     }" fill="${textColor}" opacity="${THUMBNAIL_LAYOUT.cr.opacity}">${
//       finalCr ? `CR# ${finalCr}` : ""
//     }</text>
// </svg>`;

//     if (!svg || svg.length === 0) {
//       console.error(
//         "buildThumbnailDataUrl: SVG generation failed, svg is empty",
//       );
//       return null;
//     }

//     const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
//       svg,
//     )}`;

//     if (!dataUrl || dataUrl.length < 20) {
//       console.error("buildThumbnailDataUrl: Data URL generation failed", {
//         dataUrlLength: dataUrl?.length,
//         dataUrl: dataUrl?.substring(0, 100),
//       });
//       return null;
//     }

//     return dataUrl;
//   } catch (error) {
//     console.error("buildThumbnailDataUrl: Exception caught", error);
//     return null;
//   }
// };

// Pin level bitmask values matching the other WWG apps
const PIN_BITMASK = { ibo: 2, platinum: 4, emerald: 8, diamond: 16 };

const shortPermissionLabel = (id, label) => {
  const fallbackById = {
    premier: "P",
    complimentary: "C",
    ibo: "IBO",
    platinum: "PLA",
    emerald: "EME",
    diamond: "DIA",
  };

  if (fallbackById[id]) {
    return fallbackById[id];
  }

  return String(label || "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0] || "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
};

const resolvePermissionId = ({ kind, value, label }) => {
  const normalizedKind = String(kind || "").toUpperCase();
  const normalizedLabel = String(label || "").toUpperCase();
  const numericValue = Number(value);

  if (normalizedKind === "MEMBERSHIP") {
    if (normalizedLabel.includes("PREMIER") || numericValue === 1) {
      return "premier";
    }
    if (normalizedLabel.includes("COMPLIMENT") || numericValue === 2) {
      return "complimentary";
    }
    return null;
  }

  if (normalizedKind === "PIN") {
    if (
      normalizedLabel.includes("ASSOCIATE") ||
      normalizedLabel.includes("IBO") ||
      numericValue === 3
    ) {
      return "ibo";
    }
    if (normalizedLabel.includes("PLATINUM") || numericValue === 4) {
      return "platinum";
    }
    if (normalizedLabel.includes("EMERALD") || numericValue === 5) {
      return "emerald";
    }
    if (normalizedLabel.includes("DIAMOND") || numericValue === 6) {
      return "diamond";
    }
    return null;
  }

  return null;
};

const buildPermissionUiFromSections = (visibilitySections = []) => {
  const sections = Array.isArray(visibilitySections) ? visibilitySections : [];
  const membershipSection = sections.find((section) => {
    const code = String(section?.typeCode || "").toUpperCase();
    return code === "PERMISSIONS_MEMBERSHIP";
  });
  const pinSection = sections.find((section) => {
    const code = String(section?.typeCode || "").toUpperCase();
    return code === "PERMISSIONS_PIN_LEVEL";
  });

  const membershipGroupValuesById = { premier: 1, complimentary: 2 };

  const membershipOptions = Array.isArray(membershipSection?.options)
    ? membershipSection.options
        .map((option) => {
          const id = resolvePermissionId({
            kind: "MEMBERSHIP",
            value: option?.value,
            label: option?.label,
          });
          if (!id) {
            return null;
          }

          const value = Number(option?.value);
          if (Number.isFinite(value)) {
            membershipGroupValuesById[id] = value;
          }

          const fullLabel = String(option?.label || "").trim();
          return {
            id,
            label: shortPermissionLabel(id, fullLabel),
            fullLabel,
            icon: String(option?.icon || "").trim(),
          };
        })
        .filter(Boolean)
        // Keep reverse-alphabetical order to match current design (C before P).
        // If product wants DB order later, remove this sort and keep source order.
        .sort((a, b) =>
          String(b?.fullLabel || b?.label || "").localeCompare(
            String(a?.fullLabel || a?.label || ""),
            undefined,
            { sensitivity: "base" },
          ),
        )
    : [];

  const pinLevels = Array.isArray(pinSection?.options)
    ? pinSection.options
        .map((option) => {
          const id = resolvePermissionId({
            kind: "PIN",
            value: option?.value,
            label: option?.label,
          });
          if (!id) {
            return null;
          }

          const fullLabel = String(option?.label || "").trim();
          return {
            id,
            label: shortPermissionLabel(id, fullLabel),
            fullLabel,
            icon: String(option?.icon || "").trim(),
          };
        })
        .filter(Boolean)
    : [];

  return {
    membershipOptions,
    pinLevels,
    membershipGroupValuesById,
    membershipSectionTitle:
      String(membershipSection?.label || "").trim() || "Membership Permissions",
    pinSectionTitle:
      String(pinSection?.label || "").trim() || "Pin Level Permissions",
  };
};

const findVisibilitySection = (sections = [], kind = "") => {
  const normalizedKind = String(kind || "").toUpperCase();

  if (!Array.isArray(sections) || !normalizedKind) {
    return null;
  }

  if (normalizedKind === "REUNION") {
    return (
      sections.find((section) => {
        const code = String(section?.typeCode || "").toUpperCase();
        return code.includes("REUNION");
      }) || null
    );
  }

  if (normalizedKind === "DIAMOND") {
    const byTypeCode = sections.find((section) => {
      const code = String(section?.typeCode || "").toUpperCase();
      return code === "DIAMONDSHIPS" || code === "DIAMONDS";
    });

    if (byTypeCode) {
      return byTypeCode;
    }

    return (
      sections.find((section) => {
        const code = String(section?.typeCode || "").toUpperCase();
        return code.includes("DIAMOND") && !code.includes("REUNION");
      }) ||
      sections.find((section) => {
        const label = String(section?.label || "").toUpperCase();
        return label.includes("DIAMONDSHIP") || label.includes("DIAMONDS");
      }) ||
      null
    );
  }

  return null;
};

const buildAdditionalVisibilitySections = (visibilitySections = []) => {
  // Diamondships is intentionally not ignored here: it has no dedicated UI
  // slot anymore, so it must render through this generic DB-driven path.
  const ignoredTypeCodes = new Set([
    "PERMISSIONS_MEMBERSHIP",
    "PERMISSIONS_PIN_LEVEL",
  ]);

  return (Array.isArray(visibilitySections) ? visibilitySections : [])
    .map((section) => {
      const typeCode = String(section?.typeCode || "").toUpperCase();
      if (
        !typeCode ||
        ignoredTypeCodes.has(typeCode) ||
        typeCode.includes("REUNION")
      ) {
        return null;
      }

      const selectorComponent = String(
        section?.selectorComponent || "CHECKBOX_LIST",
      ).toUpperCase();
      const supportedSelectorComponents = new Set([
        "CHECKBOX_LIST",
        "CHECKBOX",
        "CHECKBOXES",
        "MULTISELECT",
        "MULTI_SELECT",
      ]);
      if (!supportedSelectorComponents.has(selectorComponent)) {
        return null;
      }

      const title = String(section?.label || section?.type || typeCode).trim();
      const options = Array.isArray(section?.options)
        ? section.options
            .map((option) =>
              String(option?.label || option?.value || "").trim(),
            )
            .filter(Boolean)
        : [];

      if (options.length === 0) {
        return null;
      }

      return {
        typeCode,
        title,
        options,
      };
    })
    .filter(Boolean);
};

const ARCHIVE_DISPLAY_STATUS = {
  archived: "Archived",
  published: "Available",
};

const toCsvString = (value) => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.join(",");
  }

  return "";
};

const getPinLevelPermissions = (pinPermissions = {}) => {
  return Object.entries(PIN_BITMASK).reduce((sum, [key, bit]) => {
    return sum + (pinPermissions[key] ? bit : 0);
  }, 0);
};

const toApiContentSettings = (state = {}) => {
  const tags = state.tags || {};
  const membershipGroupValuesById = {
    premier: Number(state?.membershipGroupValuesById?.premier) || 1,
    complimentary: Number(state?.membershipGroupValuesById?.complimentary) || 2,
  };

  return {
    pinLevelPermissions: getPinLevelPermissions(state.pinPermissions),
    groups:
      typeof state.groups === "string"
        ? state.groups
        : [
            state.membershipPermissions?.premier
              ? String(membershipGroupValuesById.premier)
              : null,
            state.membershipPermissions?.complimentary
              ? String(membershipGroupValuesById.complimentary)
              : null,
          ]
            .filter(Boolean)
            .join(","),
    familyReunions: toCsvString(state.selectedFamilyGroups),
    diamonds: toCsvString(state.selectedDiamonds),
    additionalIbos: state.additionalIbos || "",
    tags: {
      selectedLocations: Array.isArray(tags.selectedLocations)
        ? tags.selectedLocations
        : Array.isArray(tags.locations)
        ? tags.locations
        : [],
      selectedLanguages: Array.isArray(tags.selectedLanguages)
        ? tags.selectedLanguages
        : Array.isArray(tags.languages)
        ? tags.languages
        : [],
      selectedOccupations: Array.isArray(tags.selectedOccupations)
        ? tags.selectedOccupations
        : Array.isArray(tags.occupations)
        ? tags.occupations
        : [],
      keywords: Array.isArray(tags.keywords) ? tags.keywords : [],
      hiddenTags: Array.isArray(tags.hiddenTags) ? tags.hiddenTags : [],
      byTypeCode:
        tags.byTypeCode && typeof tags.byTypeCode === "object"
          ? tags.byTypeCode
          : {},
    },
  };
};

const toApiAudioPayload = ({
  thumbnail,
  thumbnailSource,
  thumbnailBuilderData,
  audioDetails,
  files,
  archivedFiles,
  publishState,
  contentSettings,
  disclosures,
  speakers,
  isDraft,
  defaultDigitalDeliveryEnabled,
}) => {
  const resolvedPublishStatus = publishState.status || "Published";

  // Convert date-time objects or display-formatted strings to ISO 8601 strings
  const convertDateTimeToIso8601 = (dateTimeObj) => {
    if (!dateTimeObj || dateTimeObj === "N/A") return "";
    if (typeof dateTimeObj === "string") {
      // Already ISO 8601
      if (/^\d{4}-\d{2}-\d{2}T/.test(dateTimeObj)) return dateTimeObj;
      // Try to parse display formats like "11/04/2022"
      const parsed = new Date(dateTimeObj);
      return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : "";
    }
    if (!(dateTimeObj instanceof Date)) {
      // Handle {date, time} object format
      if (dateTimeObj.date) {
        const [year, month, day] = dateTimeObj.date.split("-");
        const [hours = "00", minutes = "00"] = (
          dateTimeObj.time || "00:00"
        ).split(":");
        const date = new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10),
          parseInt(hours, 10),
          parseInt(minutes, 10),
        );
        return date.toISOString();
      }
      return "";
    }
    return Number.isNaN(dateTimeObj.getTime()) ? "" : dateTimeObj.toISOString();
  };

  const normalizeFileDate = (date) => {
    if (!date || date === "N/A") return "";
    return convertDateTimeToIso8601(date);
  };

  const sanitizePersistedFile = (file = {}) => {
    const { tempFilename, localPreviewUrl, ...rest } = file;
    return {
      ...rest,
      date: normalizeFileDate(rest.date),
    };
  };

  const mediaType = audioDetails.mediaType || "Audio";
  // Derive top-level language from content settings tags (required by DigitalDelivery endpoints)
  const primaryLanguage =
    String(
      contentSettings?.tags?.selectedLanguages?.[0] || "english",
    ).toLowerCase() || "english";

  return {
    thumbnail: thumbnail?.src || "",
    thumbnailSource,
    thumbnailBuilderData: thumbnailBuilderData || null,
    isDraft,
    type: mediaType,
    category: mediaType,
    mediaTypeCode: mediaType.toLowerCase(),
    language: primaryLanguage,
    speaker:
      audioDetails.creatorSpeaker ||
      (Array.isArray(speakers) && speakers[0]?.name) ||
      "Unknown",
    contributorName:
      audioDetails.creatorSpeaker ||
      (Array.isArray(speakers) && speakers[0]?.name) ||
      "Unknown",
    detailState: {
      ...audioDetails,
      // Ensure releaseOn is saved in ISO 8601 format with datetime (YYYY-MM-DDTHH:mm:ss.000Z)
      // If user entered releaseOn, use it; otherwise fallback to publishOn on save
      releaseOn: audioDetails.releaseOn || publishState.publishOn || "",
      mediaType,
      title: audioDetails.title || "Untitled Audio",
      sku: audioDetails.sku || "N/A",
      creatorSpeaker: audioDetails.creatorSpeaker || "",
    },
    files: Array.isArray(files) ? files.map(sanitizePersistedFile) : [],
    archivedFiles: Array.isArray(archivedFiles)
      ? archivedFiles.map(sanitizePersistedFile)
      : [],
    publishState: {
      ...createDefaultPublishState(defaultDigitalDeliveryEnabled),
      ...publishState,
      status: resolvedPublishStatus,
    },
    contentSettings: toApiContentSettings(contentSettings),
    disclosures: Array.isArray(disclosures) ? disclosures : [],
    speakers: Array.isArray(speakers) ? speakers : [],
  };
};

const normalizeContentSettingsFromApi = (apiSettings, config) => {
  if (!apiSettings) return null;

  const bitmask = apiSettings.pinLevelPermissions ?? 0;
  const groupIds = String(apiSettings.groups || "")
    .split(",")
    .filter(Boolean)
    .map(Number);
  const familyReunionIds = String(apiSettings.familyReunions || "")
    .split(",")
    .filter(Boolean)
    .map(Number);
  const diamondIbos = String(apiSettings.diamonds || "")
    .split(",")
    .filter(Boolean);

  // Raw config arrays for ID→name lookups
  const rawGroups = config?.groups || [];
  const visibilitySections = Array.isArray(config?.visibilityGroupSections)
    ? config.visibilityGroupSections
    : Array.isArray(config?.visibilityGroups)
    ? config.visibilityGroups
    : [];
  const reunionSection = findVisibilitySection(visibilitySections, "REUNION");
  const diamondSection = findVisibilitySection(visibilitySections, "DIAMOND");
  const permissionUi = buildPermissionUiFromSections(visibilitySections);
  const additionalVisibilitySections =
    buildAdditionalVisibilitySections(visibilitySections);
  const visibilitySelectionsByTypeCode =
    apiSettings?.visibilitySelectionsByTypeCode &&
    typeof apiSettings.visibilitySelectionsByTypeCode === "object"
      ? apiSettings.visibilitySelectionsByTypeCode
      : {};
  const rawFamilyReunions = Array.isArray(reunionSection?.options)
    ? reunionSection.options.map((option) => option.label)
    : config?.familyReunionGroups || [];
  const rawDiamonds = Array.isArray(diamondSection?.options)
    ? diamondSection.options.map((option) => option.label)
    : config?.diamonds || [];

  // Processed option lists for display (pre-flattened to strings)
  const processed = config?._processedLists;
  const defaultTags = processed?.tags || DEFAULT_PERMISSION_STATE.tags;
  const optionGroups =
    processed?.groups ??
    rawGroups.map((g) => (typeof g === "string" ? g : g.name));
  const optionFamilyReunions =
    processed?.familyReunionGroups ??
    rawFamilyReunions.map((g) => (typeof g === "string" ? g : g.name));
  const optionDiamonds =
    processed?.diamonds ??
    rawDiamonds.map((d) => (typeof d === "string" ? d : d.name));

  return {
    membershipPermissions: {
      complimentary: groupIds.includes(
        Number(permissionUi.membershipGroupValuesById?.complimentary) || 2,
      ),
      premier: groupIds.includes(
        Number(permissionUi.membershipGroupValuesById?.premier) || 1,
      ),
    },
    pinPermissions: {
      ibo: !!(bitmask & PIN_BITMASK.ibo),
      platinum: !!(bitmask & PIN_BITMASK.platinum),
      emerald: !!(bitmask & PIN_BITMASK.emerald),
      diamond: !!(bitmask & PIN_BITMASK.diamond),
    },
    membershipOptions: permissionUi.membershipOptions,
    pinLevels: permissionUi.pinLevels,
    membershipGroupValuesById: permissionUi.membershipGroupValuesById,
    familyReunionGroups: optionFamilyReunions,
    selectedFamilyGroups: String(apiSettings.familyReunions || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    diamonds: optionDiamonds,
    selectedDiamonds: diamondIbos,
    familyReunionSectionTitle: reunionSection?.label || "",
    diamondsSectionTitle: diamondSection?.label || "",
    additionalVisibilitySections,
    visibilitySelectionsByTypeCode,
    groups: optionGroups,
    selectedGroups: groupIds
      .map((id) => rawGroups.find((g) => g.groupId === id)?.name)
      .filter(Boolean),
    additionalIbos: apiSettings.additionalIbos || "",
    tags: {
      ...defaultTags,
      ...apiSettings.tags,
    },
  };
};

const buildPermissionStateFromConfig = (config) => {
  const visibilitySections = Array.isArray(config?.visibilityGroupSections)
    ? config.visibilityGroupSections
    : Array.isArray(config?.visibilityGroups)
    ? config.visibilityGroups
    : [];
  const reunionSection = findVisibilitySection(visibilitySections, "REUNION");
  const diamondSection = findVisibilitySection(visibilitySections, "DIAMOND");
  const permissionUi = buildPermissionUiFromSections(visibilitySections);
  const additionalVisibilitySections =
    buildAdditionalVisibilitySections(visibilitySections);

  const tagDefinitions = extractSettingsTagDefinitions(config);
  const tagOptionsByTypeCode = buildTagOptionsByTypeCode(tagDefinitions);
  const hasDynamicTagSchema = tagDefinitions.length > 0;

  return {
    ...DEFAULT_PERMISSION_STATE,
    membershipOptions: permissionUi.membershipOptions,
    pinLevels: permissionUi.pinLevels,
    membershipGroupValuesById: permissionUi.membershipGroupValuesById,
    familyReunionGroups: Array.isArray(reunionSection?.options)
      ? reunionSection.options.map((option) => option.label)
      : config?.familyReunionGroups
      ? config.familyReunionGroups.map((g) =>
          typeof g === "string" ? g : g.name,
        )
      : [],
    diamonds: Array.isArray(diamondSection?.options)
      ? diamondSection.options.map((option) => option.label)
      : config?.diamonds
      ? config.diamonds.map((d) => (typeof d === "string" ? d : d.name))
      : [],
    familyReunionSectionTitle: reunionSection?.label || "",
    diamondsSectionTitle: diamondSection?.label || "",
    additionalVisibilitySections,
    visibilitySelectionsByTypeCode: {},
    groups: config?.groups
      ? config.groups.map((g) => (typeof g === "string" ? g : g.name))
      : [],
    tags: {
      ...DEFAULT_PERMISSION_STATE.tags,
      tagDefinitions,
      locationOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.LOCATION || []
        : config?.locationOptions ??
          DEFAULT_PERMISSION_STATE.tags.locationOptions,
      locations: [],
      selectedLocations: [],
      languageOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.LANGUAGE || []
        : config?.languageOptions ??
          DEFAULT_PERMISSION_STATE.tags.languageOptions,
      languages: [],
      selectedLanguages: [],
      occupationOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.OCCUPATION || []
        : config?.occupationOptions ??
          DEFAULT_PERMISSION_STATE.tags.occupationOptions,
      occupations: [],
      selectedOccupations: [],
      keywordOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.KEYWORD_TAG || []
        : config?.keywordOptions ??
          DEFAULT_PERMISSION_STATE.tags.keywordOptions,
      keywords: [],
      hiddenTagOptions: hasDynamicTagSchema
        ? tagOptionsByTypeCode.HIDDEN_TAG || []
        : config?.hiddenTagOptions ??
          DEFAULT_PERMISSION_STATE.tags.hiddenTagOptions,
      hiddenTags: [],
      byTypeCode: {},
    },
  };
};

const normalizeMediaListItem = (item, config) => {
  const apiContentSettings =
    item?.contentSettings ||
    (item?.pinLevelPermissions !== undefined || item?.groups !== undefined
      ? item
      : null);
  const normalizedContentSettings = normalizeContentSettingsFromApi(
    apiContentSettings,
    config,
  );

  return {
    ...item,
    pinPermissions:
      normalizedContentSettings?.pinPermissions || item?.pinPermissions || {},
    membershipPermissions:
      normalizedContentSettings?.membershipPermissions ||
      item?.membershipPermissions ||
      {},
  };
};

export function Media({
  headerTitle = "DreamCast - Media",
  startInCreateView = false,
  defaultDigitalDeliveryEnabled = false,
  addButtonLabel = "Add New Media",
  onCreateCancel,
  onCreateComplete,
  initialAudios = [],
  initialEditingAudioId = null,
  preferInitialEditingData = false,
  initialContentSettingsConfig = null,
  disableInitialFetch = false,
  apiConfig = {},
}) {
  const initialPrefetchedEditingItem =
    preferInitialEditingData && initialEditingAudioId != null
      ? initialAudios.find(
          (item) => item.mediaItemId === initialEditingAudioId,
        ) || null
      : null;
  const initialPermissionState = buildPermissionStateFromConfig(
    initialContentSettingsConfig,
  );

  const resolvedApi = {
    fetchList: fetchDreamcastMedia,
    fetchById: fetchDreamcastMediaById,
    fetchFilesById: fetchDreamcastMediaFilesById,
    create: createDreamcastMedia,
    update: updateDreamcastMedia,
    duplicate: duplicateDreamcastMedia,
    setArchiveState: setDreamcastMediaArchiveState,
    ...apiConfig,
  };
  const history = useHistory();
  const location = useLocation();
  const isSelectionMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("mode") === "selection";
  }, [location.search]);
  const selectionSource = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("source") || "";
  }, [location.search]);
  const isSpeakerMediaSelection =
    isSelectionMode && selectionSource === "speakers";
  const [isCreateView, setIsCreateView] = useState(
    startInCreateView || Boolean(initialPrefetchedEditingItem),
  );
  const [editingAudioId, setEditingAudioId] = useState(
    initialPrefetchedEditingItem?.mediaItemId ?? null,
  );
  const pageRef = useRef(null);
  const [hasAppliedInitialEdit, setHasAppliedInitialEdit] = useState(false);
  const [editingWasDraft, setEditingWasDraft] = useState(
    Boolean(initialPrefetchedEditingItem?.isDraft),
  );
  const [audios, setAudios] = useState(initialAudios);
  const [searchQuery, setSearchQuery] = useState(
    () => location.state?.speakerFormState?.speakerAudioSearch || "",
  );
  const [filters, setFilters] = useState({
    Status: ["published", "upcoming", "in-review"],
  });
  const [sortBy, setSortBy] = useState("date-newest");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  const [apiError, setApiError] = useState("");
  const [fileUploadRequired, setFileUploadRequired] = useState(false);
  const [skuError, setSkuError] = useState("");
  const [skuDuplicateError, setSkuDuplicateError] = useState(false);
  const [showSkuDuplicateModal, setShowSkuDuplicateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [requiredFieldErrors, setRequiredFieldErrors] = useState({});
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [duplicatingItemId, setDuplicatingItemId] = useState(null);
  const [duplicationInProgress, setDuplicationInProgress] = useState(new Set());
  const [filesUploading, setFilesUploading] = useState(0);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const isUploadInProgress = filesUploading > 0 || thumbnailUploading;
  // Set when the user already confirmed leaving via the Cancel modal, so the route Prompt doesn't ask twice.
  const leaveConfirmedRef = useRef(false);

  useEffect(() => {
    if (!isCreateView || !isUploadInProgress) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isCreateView, isUploadInProgress]);

  const [loadingAudioId, setLoadingAudioId] = useState(null);
  const [archivingAudioId, setArchivingAudioId] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [editAttemptedDuringMediaIds, setEditAttemptedDuringMediaIds] =
    useState(new Set());
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);

  const [audioDetails, setAudioDetails] = useState(() => {
    const baseDetails = {
      ...DEFAULT_AUDIO_DETAILS,
      ...withoutMarkAsNewFields(initialPrefetchedEditingItem?.detailState),
    };

    // If releaseOn is empty, fallback to publishOn (applies for both create and edit modes)
    if (
      !baseDetails.releaseOn &&
      initialPrefetchedEditingItem?.publishState?.publishOn
    ) {
      baseDetails.releaseOn =
        initialPrefetchedEditingItem.publishState.publishOn;
    }

    return baseDetails;
  });

  const [contentSettingsConfig, setContentSettingsConfig] = useState(
    initialContentSettingsConfig,
  );

  const mediaSourceOptions = useMemo(
    () => extractMediaSourceOptionsFromConfig(contentSettingsConfig),
    [contentSettingsConfig],
  );
  const mediaTypeOptions = useMemo(
    () => extractMediaTypeOptionsFromConfig(contentSettingsConfig),
    [contentSettingsConfig],
  );
  const mediaTemplates = useMemo(
    () => extractMediaTemplatesFromConfig(contentSettingsConfig),
    [contentSettingsConfig],
  );

  // Map Media Type to File Type for the FileUploadSection
  const defaultFileType = useMemo(() => {
    const mediaType = audioDetails.mediaType || "Audio";
    if (mediaType === "Video") {
      return "Video";
    }
    // "Audio" and "Music" both default to "Audio"
    return "Audio";
  }, [audioDetails.mediaType]);

  const basePermissionState = useMemo(
    () => buildPermissionStateFromConfig(contentSettingsConfig),
    [contentSettingsConfig],
  );

  const [thumbnail, setThumbnail] = useState(
    initialPrefetchedEditingItem?.thumbnailPath
      ? {
          src: initialPrefetchedEditingItem.thumbnailPath,
          alt: `${initialPrefetchedEditingItem.title} thumbnail`,
        }
      : null,
  );
  const [thumbnailSource, setThumbnailSource] = useState(
    initialPrefetchedEditingItem?.thumbnailSource ||
      (initialPrefetchedEditingItem?.thumbnailPath ? "custom" : "none"),
  );
  const [thumbnailTempFilename, setThumbnailTempFilename] = useState(null);
  const [thumbnailPrevUrl, setThumbnailPrevUrl] = useState(
    initialPrefetchedEditingItem?.thumbnailPath || "",
  );
  const [thumbnailBuilderOpen, setThumbnailBuilderOpen] = useState(
    initialPrefetchedEditingItem?.thumbnailSource === "generated",
  );
  const [thumbnailBuilderData, setThumbnailBuilderData] = useState(
    initialPrefetchedEditingItem?.thumbnailBuilderData || null,
  );
  // Unsaved builder edits, kept so navigating away and back does not discard them.
  const [thumbnailBuilderDraft, setThumbnailBuilderDraft] = useState(null);

  const [files, setFiles] = useState(
    Array.isArray(initialPrefetchedEditingItem?.files)
      ? initialPrefetchedEditingItem.files.map((file, index) =>
          index === 0
            ? {
                ...file,
                naCrNumber:
                  initialPrefetchedEditingItem.detailState?.naCrNumber ||
                  file.naCrNumber,
                anzCrNumber:
                  initialPrefetchedEditingItem.detailState?.anzCrNumber ||
                  file.anzCrNumber,
              }
            : file,
        )
      : [],
  );
  const [archivedFiles, setArchivedFiles] = useState(
    Array.isArray(initialPrefetchedEditingItem?.archivedFiles)
      ? initialPrefetchedEditingItem.archivedFiles
      : [],
  );
  const [removedArchivedFiles, setRemovedArchivedFiles] = useState([]);
  const [activeMediaFile, setActiveMediaFile] = useState(null); // Track current active file
  const [pendingActiveFileOp, setPendingActiveFileOp] = useState(null); // Pending active file move
  const [restoredMediaFile, setRestoredMediaFile] = useState(null); // Track file being restored from archive

  const [publishState, setPublishState] = useState(
    initialPrefetchedEditingItem
      ? {
          ...createDefaultPublishState(defaultDigitalDeliveryEnabled),
          ...initialPrefetchedEditingItem.publishState,
        }
      : createDefaultPublishState(defaultDigitalDeliveryEnabled),
  );

  const [contentSettings, setContentSettings] = useState(() =>
    initialPrefetchedEditingItem
      ? normalizeContentSettingsFromApi(
          initialPrefetchedEditingItem.contentSettings,
          {
            ...contentSettingsConfig,
            _processedLists: initialPermissionState,
          },
        ) || initialPermissionState
      : initialPermissionState,
  );
  const [speakers, setSpeakers] = useState(
    Array.isArray(initialPrefetchedEditingItem?.speakers)
      ? initialPrefetchedEditingItem.speakers
      : [],
  );
  const [mediaDisclosures, setMediaDisclosures] = useState(() =>
    withDisclosureAssignmentIds(initialPrefetchedEditingItem?.disclosures),
  );
  const [publishedDisclosures, setPublishedDisclosures] = useState([]);
  const [pendingFileOps, setPendingFileOps] = useState(null);
  const filesRef = useRef(
    Array.isArray(initialPrefetchedEditingItem?.files)
      ? initialPrefetchedEditingItem.files
      : [],
  );
  const archivedFilesRef = useRef(
    Array.isArray(initialPrefetchedEditingItem?.archivedFiles)
      ? initialPrefetchedEditingItem.archivedFiles
      : [],
  );
  const startingActiveFilenameRef = useRef(null);
  const thumbnailBuilderRef = useRef(null);

  // ---- Undo / History state (ported from the WWG Store product editor) ----
  // `historyBaseline` snapshots every editable field when the edit/create view
  // opens; `isFormDirty` is DERIVED by comparing the live snapshot against it.
  const [historyBaseline, setHistoryBaseline] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [pendingUndo, setPendingUndo] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);

  // Serialized snapshot of every user-editable field in the edit form. Single
  // source of truth for "has the form changed" and the restore source for Undo.
  const buildFormSnapshot = React.useCallback(
    () => ({
      details: audioDetails,
      thumbnail,
      thumbnailSource,
      thumbnailBuilderData,
      files,
      archivedFiles,
      publishState,
      contentSettings,
      disclosures: mediaDisclosures,
      speakers,
    }),
    [
      audioDetails,
      thumbnail,
      thumbnailSource,
      thumbnailBuilderData,
      files,
      archivedFiles,
      publishState,
      contentSettings,
      mediaDisclosures,
      speakers,
    ],
  );

  const isFormDirty = useMemo(() => {
    if (historyBaseline === null) return false;
    const snap = buildFormSnapshot();
    // Compare file lists separately via stable identity (signed preview URLs
    // rotate tokens); everything else via sorted-key JSON.
    const stripFiles = ({ files: f, archivedFiles: af, ...rest }) => rest;
    return (
      JSON.stringify(sortObjectKeys(stripFiles(snap))) !==
        JSON.stringify(sortObjectKeys(stripFiles(historyBaseline))) ||
      fileListKey(snap.files) !== fileListKey(historyBaseline.files) ||
      fileListKey(snap.archivedFiles) !==
        fileListKey(historyBaseline.archivedFiles)
    );
  }, [historyBaseline, buildFormSnapshot]);

  // Latest-snapshot ref so the capture timer below reads current form state
  // without re-arming on every keystroke.
  const buildFormSnapshotRef = useRef(buildFormSnapshot);
  useEffect(() => {
    buildFormSnapshotRef.current = buildFormSnapshot;
  });

  // Seed the baseline shortly after the edit/create view opens; clear all undo
  // state when it closes. openEditView flips isCreateView LAST (after all data
  // setters), and mount-time normalizations in child cards (e.g. PublishCard
  // deriving "Scheduled" from a future publishOn) complete within a frame or
  // two of mounting — so a short fixed delay captures the settled form.
  // Deliberately NOT rescheduled on later state changes, so early user edits
  // can never be absorbed into the baseline.
  useEffect(() => {
    if (!isCreateView) {
      setHistoryBaseline(null);
      setShowHistory(false);
      setPendingUndo(false);
      setPendingCancel(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setHistoryBaseline((prev) => prev ?? buildFormSnapshotRef.current());
    }, 120);
    return () => clearTimeout(timer);
  }, [isCreateView]);

  // Push a snapshot back into the live form state. Shared by Undo All and the
  // per-field Restore in the history overlay.
  const restoreSnapshot = React.useCallback((snap) => {
    if (!snap) return;
    setAudioDetails(snap.details);
    setThumbnail(snap.thumbnail);
    setThumbnailSource(snap.thumbnailSource || "none");
    setThumbnailTempFilename(null);
    setThumbnailPrevUrl(snap.thumbnail?.src || "");
    setThumbnailBuilderData(snap.thumbnailBuilderData);
    setThumbnailBuilderDraft(null);
    setThumbnailBuilderOpen(snap.thumbnailSource === "generated");
    thumbnailBuilderRef.current?.setData?.(snap.thumbnailBuilderData);
    filesRef.current = snap.files;
    setFiles(snap.files);
    setArchivedFiles(snap.archivedFiles);
    setRemovedArchivedFiles([]);
    setPendingActiveFileOp(null);
    setRestoredMediaFile(null);
    setPublishState(snap.publishState);
    setContentSettings(snap.contentSettings);
    setMediaDisclosures(withDisclosureAssignmentIds(snap.disclosures));
    setSpeakers(snap.speakers);
  }, []);

  // Revert every editable field to the opened-with snapshot. Destructive, so
  // gated behind a confirmation modal (see pendingUndo).
  const confirmUndo = () => {
    if (historyBaseline === null) {
      setPendingUndo(false);
      return;
    }
    restoreSnapshot(historyBaseline);
    setPendingUndo(false);
  };

  // Restore a single field (dotted snapshot key) to its baseline value.
  const handleRestoreField = React.useCallback(
    (fieldKey, value) => {
      if (fieldKey === "thumbnail") {
        setThumbnail(value);
        setThumbnailSource(historyBaseline?.thumbnailSource || "none");
        setThumbnailTempFilename(null);
        setThumbnailPrevUrl(value?.src || "");
        return;
      }
      if (fieldKey === "thumbnailBuilderData") {
        setThumbnailBuilderData(value);
        setThumbnailBuilderDraft(null);
        setThumbnailSource(historyBaseline?.thumbnailSource || "none");
        setThumbnailBuilderOpen(
          historyBaseline?.thumbnailSource === "generated",
        );
        thumbnailBuilderRef.current?.setData?.(value);
        return;
      }
      if (fieldKey === "files") {
        filesRef.current = value;
        setFiles(value);
        return;
      }
      if (fieldKey === "archivedFiles") {
        setArchivedFiles(value);
        setRemovedArchivedFiles([]);
        return;
      }
      if (fieldKey === "speakers") {
        setSpeakers(value);
        return;
      }
      if (fieldKey === "disclosures") {
        setMediaDisclosures(withDisclosureAssignmentIds(value));
        return;
      }

      const [group, ...restPath] = fieldKey.split(".");
      const subPath = restPath.join(".");
      if (group === "details") {
        setAudioDetails((prev) => setNestedValue(prev, subPath, value));
        // Editing title/creatorSpeaker/sku mirrors the value into
        // thumbnailBuilderData.details (see AudioDetailsForm onChange), so
        // restoring one of those fields must also revert the mirror or the
        // form stays dirty after the visible value returns.
        if (
          ["title", "creatorSpeaker", "sku"].includes(subPath) &&
          historyBaseline
        ) {
          setThumbnailBuilderData(historyBaseline.thumbnailBuilderData);
        }
      } else if (group === "publishState") {
        setPublishState((prev) => setNestedValue(prev, subPath, value));
      } else if (group === "contentSettings") {
        setContentSettings((prev) => setNestedValue(prev, subPath, value));
      }
    },
    [historyBaseline],
  );

  const hasPendingFileOps = (mediaId) => {
    if (!pendingFileOps && !pendingActiveFileOp && !restoredMediaFile)
      return false;
    const mediaIdStr = String(mediaId);
    return (
      String(pendingFileOps?.archivedFiles?.mediaId) === mediaIdStr ||
      String(pendingFileOps?.image?.mediaId) === mediaIdStr ||
      String(pendingActiveFileOp?.mediaId) === mediaIdStr
    );
  };

  const loadAudios = React.useCallback(async () => {
    setIsLoadingList(true);
    try {
      const startTime = Date.now();
      const response = await resolvedApi.fetchList();
      const nextAudios = placeMediaCopiesBeforeSources(
        Array.isArray(response?.data) ? response.data : [],
      );
      setAudios(nextAudios);

      const elapsed = Date.now() - startTime;
      if (elapsed < 500) {
        await new Promise((resolve) => setTimeout(resolve, 500 - elapsed));
      }

      return nextAudios;
    } finally {
      setIsLoadingList(false);
    }
  }, [resolvedApi.fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (disableInitialFetch || initialContentSettingsConfig) {
      return;
    }

    let isSubscribed = true;

    fetchDreamcastContentSettingsConfig()
      .then((response) => {
        const payload = response?.data || response;
        if (!isSubscribed || !payload) {
          return;
        }

        setContentSettingsConfig(payload);
      })
      .catch(() => {
        // Keep fallbacks when config fetch is unavailable.
      });

    return () => {
      isSubscribed = false;
    };
  }, [disableInitialFetch, initialContentSettingsConfig]);

  // Refetch config when a new tag is created
  const handleTagCreated = useCallback(() => {
    fetchDreamcastContentSettingsConfig()
      .then((response) => {
        const payload = response?.data || response;
        if (payload) {
          setContentSettingsConfig(payload);
        }
      })
      .catch(() => {
        // Keep fallbacks when config fetch is unavailable.
      });
  }, []);

  React.useEffect(() => {
    if (disableInitialFetch) return;

    let subscribed = true;
    fetchDreamcastDisclosures({ publishedOnly: true })
      .then((response) => {
        if (!subscribed) return;
        const data = Array.isArray(response?.data) ? response.data : [];
        setPublishedDisclosures(
          data.map((item) => ({
            id: item.id || item.mediaItemId,
            title: item.title,
            status: item.status || item.publishState?.status || "Published",
            type: item.type || item.mediaType || "Audio",
            previewUrl: item.previewUrl || item.mediaUrl || "",
          })),
        );
      })
      .catch(() => {
        if (subscribed) setPublishedDisclosures([]);
      });

    return () => {
      subscribed = false;
    };
  }, [disableInitialFetch]);

  React.useEffect(() => {
    setContentSettings((current) => {
      const baseTags = basePermissionState?.tags || {};
      const currentTags = current?.tags || {};
      const baseTagDefinitions = Array.isArray(baseTags.tagDefinitions)
        ? baseTags.tagDefinitions
        : [];
      const baseFamilyReunionGroups = Array.isArray(
        basePermissionState?.familyReunionGroups,
      )
        ? basePermissionState.familyReunionGroups
        : [];
      const baseDiamonds = Array.isArray(basePermissionState?.diamonds)
        ? basePermissionState.diamonds
        : [];
      const baseGroups = Array.isArray(basePermissionState?.groups)
        ? basePermissionState.groups
        : [];
      const baseAdditionalVisibilitySections = Array.isArray(
        basePermissionState?.additionalVisibilitySections,
      )
        ? basePermissionState.additionalVisibilitySections
        : [];
      const currentVisibilitySelectionsByTypeCode =
        current?.visibilitySelectionsByTypeCode &&
        typeof current.visibilitySelectionsByTypeCode === "object"
          ? current.visibilitySelectionsByTypeCode
          : {};
      const baseMembershipOptions = Array.isArray(
        basePermissionState?.membershipOptions,
      )
        ? basePermissionState.membershipOptions
        : [];
      const basePinLevels = Array.isArray(basePermissionState?.pinLevels)
        ? basePermissionState.pinLevels
        : [];
      const baseMembershipGroupValuesById =
        basePermissionState?.membershipGroupValuesById &&
        typeof basePermissionState.membershipGroupValuesById === "object"
          ? basePermissionState.membershipGroupValuesById
          : {};

      return {
        ...basePermissionState,
        ...current,
        // Keep DB-derived option lists as the source of truth after config fetch.
        familyReunionGroups: baseFamilyReunionGroups,
        diamonds: baseDiamonds,
        groups: baseGroups,
        additionalVisibilitySections: baseAdditionalVisibilitySections,
        visibilitySelectionsByTypeCode: currentVisibilitySelectionsByTypeCode,
        membershipOptions: baseMembershipOptions,
        pinLevels: basePinLevels,
        membershipGroupValuesById: baseMembershipGroupValuesById,
        familyReunionSectionTitle:
          basePermissionState?.familyReunionSectionTitle ||
          current?.familyReunionSectionTitle,
        diamondsSectionTitle:
          basePermissionState?.diamondsSectionTitle ||
          current?.diamondsSectionTitle,
        tags: {
          ...baseTags,
          ...currentTags,
          locationOptions:
            Array.isArray(baseTags.locationOptions) &&
            baseTags.locationOptions.length > 0
              ? baseTags.locationOptions
              : currentTags.locationOptions || [],
          languageOptions:
            Array.isArray(baseTags.languageOptions) &&
            baseTags.languageOptions.length > 0
              ? baseTags.languageOptions
              : currentTags.languageOptions || [],
          occupationOptions:
            Array.isArray(baseTags.occupationOptions) &&
            baseTags.occupationOptions.length > 0
              ? baseTags.occupationOptions
              : currentTags.occupationOptions || [],
          keywordOptions:
            Array.isArray(baseTags.keywordOptions) &&
            baseTags.keywordOptions.length > 0
              ? baseTags.keywordOptions
              : currentTags.keywordOptions || [],
          hiddenTagOptions:
            Array.isArray(baseTags.hiddenTagOptions) &&
            baseTags.hiddenTagOptions.length > 0
              ? baseTags.hiddenTagOptions
              : currentTags.hiddenTagOptions || [],
          // Keep DB-provided tag schema once available.
          tagDefinitions:
            baseTagDefinitions.length > 0
              ? baseTagDefinitions
              : currentTags.tagDefinitions || [],
        },
      };
    });
  }, [basePermissionState]);

  React.useEffect(() => {
    if (disableInitialFetch) {
      return;
    }

    loadAudios().catch((error) => {
      setApiError(error.message || "Failed to load media from local API.");
    });
  }, [disableInitialFetch, loadAudios]);

  React.useEffect(() => {
    if (!location.state?.fromSpeakerSelection) {
      return;
    }

    const mediaFormState =
      location.state?.mediaFormState &&
      typeof location.state.mediaFormState === "object"
        ? location.state.mediaFormState
        : null;

    if (mediaFormState) {
      setEditingAudioId(mediaFormState.editingAudioId ?? null);
      setEditingWasDraft(Boolean(mediaFormState.editingWasDraft));
      setAudioDetails({
        ...DEFAULT_AUDIO_DETAILS,
        ...withoutMarkAsNewFields(mediaFormState.audioDetails),
      });

      setThumbnail(mediaFormState.thumbnail || null);
      const restoredThumbnailSource =
        mediaFormState.thumbnailSource ||
        (mediaFormState.thumbnail ? "custom" : "none");
      setThumbnailSource(restoredThumbnailSource);
      setThumbnailTempFilename(mediaFormState.thumbnailTempFilename || null);
      setThumbnailPrevUrl(mediaFormState.thumbnailPrevUrl || "");
      setThumbnailBuilderData(mediaFormState.thumbnailBuilderData || null);
      setThumbnailBuilderDraft(mediaFormState.thumbnailBuilderDraft || null);
      setThumbnailBuilderOpen(
        mediaFormState.thumbnailSource
          ? mediaFormState.thumbnailSource === "generated"
          : Boolean(mediaFormState.thumbnailBuilderOpen),
      );

      const nextFiles = Array.isArray(mediaFormState.files)
        ? mediaFormState.files
        : [];
      const nextArchivedFiles = Array.isArray(mediaFormState.archivedFiles)
        ? mediaFormState.archivedFiles
        : [];

      filesRef.current = nextFiles;
      archivedFilesRef.current = nextArchivedFiles;
      setFiles(nextFiles);
      setArchivedFiles(nextArchivedFiles);
      setRemovedArchivedFiles(
        Array.isArray(mediaFormState.removedArchivedFiles)
          ? mediaFormState.removedArchivedFiles
          : [],
      );

      setActiveMediaFile(mediaFormState.activeMediaFile || null);
      setRestoredMediaFile(mediaFormState.restoredMediaFile || null);
      startingActiveFilenameRef.current =
        mediaFormState.startingActiveFilename || null;

      setPublishState({
        ...createDefaultPublishState(defaultDigitalDeliveryEnabled),
        ...(mediaFormState.publishState || {}),
      });
      setContentSettings(mediaFormState.contentSettings || basePermissionState);
      setMediaDisclosures(
        withDisclosureAssignmentIds(mediaFormState.disclosures),
      );
      setApiError(mediaFormState.apiError || "");
      if (mediaFormState.historyBaseline) {
        setHistoryBaseline(mediaFormState.historyBaseline);
      }
    }

    if (Array.isArray(location.state.selectedSpeakers)) {
      setSpeakers(location.state.selectedSpeakers);
    } else if (mediaFormState && Array.isArray(mediaFormState.speakers)) {
      setSpeakers(mediaFormState.speakers);
    }

    setIsCreateView(true);
    history.replace({ ...location, state: undefined });
  }, [basePermissionState, defaultDigitalDeliveryEnabled, history, location]);

  React.useEffect(() => {
    if (!isSpeakerMediaSelection) {
      return;
    }

    const preselected = Array.isArray(location.state?.preselectedMediaIds)
      ? location.state.preselectedMediaIds.map(String)
      : [];
    setSelectedMediaIds(preselected);
  }, [isSpeakerMediaSelection, location.state]);

  const cameFromSpeakerSelection = Boolean(
    location.state?.fromSpeakerSelection,
  );

  const sortedAndFilteredAudios = useMemo(() => {
    const hasFuturePublishOn = (item) => {
      const publishOnValue = item?.PublishOn || item?.publishOn || item?.date;
      if (!publishOnValue) {
        return false;
      }

      const publishOnDate = new Date(publishOnValue);
      if (!Number.isFinite(publishOnDate.getTime())) {
        return false;
      }

      return publishOnDate.getTime() > Date.now();
    };

    const matchesFilterGroup = (item, sectionKey, itemValue) => {
      const selectedValues = filters[sectionKey] || [];
      if (!selectedValues.length) return true;

      if (sectionKey === "Language") {
        const normalizedItemValue = String(itemValue || "").toLowerCase();
        const normalizedSelectedValues = selectedValues.map((value) =>
          String(value || "").toLowerCase(),
        );
        return normalizedSelectedValues.includes(normalizedItemValue);
      }

      if (sectionKey === "Category") {
        const normalizedItemValue = String(itemValue || "").toLowerCase();
        const normalizedSelectedValues = selectedValues.map((value) =>
          String(value || "").toLowerCase(),
        );
        return normalizedSelectedValues.includes(normalizedItemValue);
      }

      return selectedValues.includes(itemValue);
    };

    const normalizedQuery = searchQuery.trim().toLowerCase();

    const filtered = audios.filter((item) => {
      if (normalizedQuery) {
        const tagsString = Array.isArray(item.tags) ? item.tags.join(" ") : "";
        const itemHiddenTagsString = Array.isArray(
          item.contentSettings?.tags?.hiddenTags,
        )
          ? item.contentSettings.tags.hiddenTags.join(" ")
          : "";
        const searchable =
          `${item.title} ${item.contributorName} ${item.sku} ${tagsString} ${itemHiddenTagsString}`.toLowerCase();
        if (!searchable.includes(normalizedQuery)) {
          return false;
        }
      }

      if (!matchesFilterGroup(item, "Type", getMediaTypeGroup(item)))
        return false;
      if (!matchesCategoryFilter(item, filters.Category)) return false;
      if (!matchesFilterGroup(item, "Language", item.language)) return false;

      const selectedStatuses = Array.isArray(filters.Status)
        ? filters.Status
        : [];
      if (selectedStatuses.length > 0) {
        const normalizedStatuses = selectedStatuses.map((status) =>
          String(status || "").toLowerCase(),
        );
        const workflowStatus = String(item.workflowStatus || "").toLowerCase();

        const matchesNonScheduledStatus = normalizedStatuses.some(
          (status) => status !== "upcoming" && status === workflowStatus,
        );
        const matchesScheduledStatus =
          normalizedStatuses.includes("upcoming") &&
          (workflowStatus === "upcoming" ||
            (hasFuturePublishOn(item) && workflowStatus !== "archived"));

        if (!matchesNonScheduledStatus && !matchesScheduledStatus) {
          return false;
        }
      }

      const selectedTags = filters.Tag || [];
      if (selectedTags.length > 0) {
        const normalizedTags = item.tags.map((tag) => tag.toLowerCase());
        const hasTag = selectedTags.some((tag) =>
          normalizedTags.includes(String(tag).toLowerCase()),
        );
        if (!hasTag) return false;
      }

      return true;
    });

    const sorted = [...filtered];

    if (sortBy === "date-newest") {
      sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
    }

    if (sortBy === "date-oldest") {
      sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    if (sortBy === "audio-asc") {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    }

    if (sortBy === "audio-desc") {
      sorted.sort((a, b) => b.title.localeCompare(a.title));
    }

    return sorted;
  }, [audios, filters, searchQuery, sortBy, contentSettings?.tags?.hiddenTags]);

  const selectionAudios = useMemo(() => {
    return sortedAndFilteredAudios;
  }, [sortedAndFilteredAudios]);

  // Pagination logic
  const paginatedAudios = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return (isSelectionMode ? selectionAudios : sortedAndFilteredAudios).slice(
      startIndex,
      endIndex,
    );
  }, [currentPage, sortedAndFilteredAudios, selectionAudios, isSelectionMode]);

  const totalPages = Math.ceil(
    (isSelectionMode ? selectionAudios : sortedAndFilteredAudios).length /
      ITEMS_PER_PAGE,
  );

  // Reset to page 1 when filters/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchQuery, sortBy, isSelectionMode]);

  // Clear search bar when a filter is selected
  useEffect(() => {
    const filtersHaveSelection = Object.keys(filters || {}).some((key) =>
      key === "Status"
        ? false
        : Array.isArray(filters[key]) && filters[key].length > 0,
    );
    if (filtersHaveSelection && searchQuery) {
      setSearchQuery("");
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visible list for current context (selection mode vs normal)
  const _visibleList = isSelectionMode
    ? selectionAudios
    : sortedAndFilteredAudios;
  // Consider filters (e.g., Category) as an active query for no-results display.
  const filtersHaveSelection = Object.keys(filters || {}).some((key) =>
    key === "Status"
      ? false
      : Array.isArray(filters[key]) && filters[key].length > 0,
  );

  // Only show the SearchBar-attached "No results" when the user has typed
  // something in the search box AND no filters are applied. If filters are
  // also applied, show the page-level message instead.
  const searchBarNoResults =
    _visibleList.length === 0 &&
    String(searchQuery || "").trim() !== "" &&
    !filtersHaveSelection;

  const filtersNoResults = _visibleList.length === 0 && filtersHaveSelection;

  const filterSections = useMemo(() => {
    const observedLanguages = (Array.isArray(audios) ? audios : [])
      .map((item) => String(item?.language || "").trim())
      .filter(Boolean);
    const configuredLanguages = Array.isArray(
      basePermissionState?.tags?.languageOptions,
    )
      ? basePermissionState.tags.languageOptions
      : [];

    const configuredTagOptions = Array.isArray(
      basePermissionState?.tags?.keywordOptions,
    )
      ? basePermissionState.tags.keywordOptions
      : [];
    const observedTagOptions = (Array.isArray(audios) ? audios : [])
      .flatMap((item) => (Array.isArray(item?.tags) ? item.tags : []))
      .map((tag) => String(tag || "").trim())
      .filter(Boolean);

    const languageOptionsSource =
      configuredLanguages.length > 0 ? configuredLanguages : observedLanguages;
    const tagOptionsSource =
      configuredTagOptions.length > 0
        ? configuredTagOptions
        : observedTagOptions;

    const normalizedLanguageOptions = Array.from(
      new Map(
        languageOptionsSource
          .map((language) => String(language || "").trim())
          .filter(Boolean)
          .map((language) => [
            language.toLowerCase(),
            { label: language, value: language.toLowerCase() },
          ]),
      ).values(),
    );

    const normalizedTagOptions = Array.from(
      new Map(
        tagOptionsSource
          .map((tag) => String(tag || "").trim())
          .filter(Boolean)
          .map((tag) => [
            tag.toLowerCase(),
            { label: tag, value: tag.toLowerCase() },
          ]),
      ).values(),
    );

    return FILTER_SECTIONS.map((section) =>
      section.label === "Language"
        ? {
            ...section,
            options: normalizedLanguageOptions,
          }
        : section.label === "Tag"
        ? {
            ...section,
            options: normalizedTagOptions,
          }
        : section,
    );
  }, [
    audios,
    basePermissionState?.tags?.keywordOptions,
    basePermissionState?.tags?.languageOptions,
  ]);

  const contentSettingsForRender = {
    ...basePermissionState,
    ...(contentSettings || {}),
    additionalVisibilitySections:
      contentSettings?.additionalVisibilitySections?.length > 0
        ? contentSettings.additionalVisibilitySections
        : basePermissionState.additionalVisibilitySections,
  };

  const resetCreateForm = () => {
    setEditingAudioId(null);
    setEditingWasDraft(false);
    setAudioDetails(DEFAULT_AUDIO_DETAILS);

    setThumbnail(null);
    setThumbnailSource("none");
    setThumbnailTempFilename(null);
    setThumbnailPrevUrl("");
    setThumbnailBuilderOpen(false);
    setThumbnailBuilderData(null);
    setThumbnailBuilderDraft(null);

    setFiles([]);
    setArchivedFiles([]);
    setRemovedArchivedFiles([]);
    setActiveMediaFile(null);
    setRestoredMediaFile(null);
    filesRef.current = [];
    archivedFilesRef.current = [];
    startingActiveFilenameRef.current = null;

    setPublishState(createDefaultPublishState(defaultDigitalDeliveryEnabled));
    setContentSettings(basePermissionState);
    setMediaDisclosures([]);
    setSpeakers([]);

    // Reset validation state
    setAttemptedSave(false);
    setRequiredFieldErrors({});
    setSkuDuplicateError(false);
    setSkuError("");
    setApiError("");
  };

  // Validation function for required fields
  const fileUploadSectionRef = useRef(null);

  const createFromCurrentForm = async (mode = "save") => {
    // Mark that user attempted to save
    setAttemptedSave(true);

    // Validate required fields
    const errors = {};
    if (!audioDetails.title || audioDetails.title.trim() === "") {
      errors.title = true;
    }
    if (
      !audioDetails.creatorSpeaker ||
      audioDetails.creatorSpeaker.trim() === ""
    ) {
      errors.creatorSpeaker = true;
    }
    if (!audioDetails.topic || audioDetails.topic.trim() === "") {
      errors.topic = true;
    }
    if (
      !audioDetails.sku ||
      audioDetails.sku.trim() === "" ||
      audioDetails.sku === "N/A"
    ) {
      errors.sku = true;
    }
    setRequiredFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setApiError(
        "Please fill in all required fields: Title, Speaker, Event, and SKU.",
      );
      return;
    }

    if (thumbnailUploading || filesUploading > 0) {
      setApiError("Please wait for uploads to complete before saving.");
      return;
    }

    const hasUploadedMediaFile =
      Array.isArray(files) &&
      files.some(
        (file) =>
          file &&
          typeof file === "object" &&
          (file.name || file.tempFilename || file.previewUrl || file.id),
      );

    if (!hasUploadedMediaFile) {
      setFileUploadRequired(true);
      setApiError("Upload an audio or video file before saving.");
      requestAnimationFrame(() => {
        fileUploadSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
      return;
    }

    setFileUploadRequired(false);

    const incompatibleDisclosures = findIncompatibleDisclosures(
      mediaDisclosures,
      publishedDisclosures,
      audioDetails.mediaType,
    );
    if (incompatibleDisclosures.length > 0) {
      setApiError(
        `Video disclosures can only be added to video media: ${incompatibleDisclosures
          .map((item) => item.title || "Untitled")
          .join(", ")}. Remove them or change the file type to Video.`,
      );
      return;
    }

    const disclosureMissingPlacement = (mediaDisclosures || []).find(
      (item) => item && !item.playAtStart && !item.playAtEnd,
    );
    if (disclosureMissingPlacement) {
      setApiError(
        `Choose Start, End, or both for disclosure "${
          disclosureMissingPlacement.title || "Untitled"
        }".`,
      );
      return;
    }

    // SKU is productions-owned, required, and never autogenerated.
    const skuValue = String(audioDetails?.sku || "").trim();
    if (!skuValue || skuValue === "N/A") {
      setApiError("SKU is required.");
      return;
    }

    let createdRecordId = null;
    try {
      setApiError("");
      setIsSaving(true);

      let resolvedThumbnail = thumbnail;
      let resolvedThumbnailSource = thumbnailSource;
      let resolvedThumbnailBuilderData = thumbnailBuilderData;
      let resolvedThumbnailTempFilename = thumbnailTempFilename;

      if (thumbnailBuilderOpen) {
        const builderData =
          await thumbnailBuilderRef.current?.getSavePayload?.();

        if (!builderData) {
          throw new Error("Unable to render the current thumbnail.");
        }

        if (builderData.isThumbnailEmpty) {
          resolvedThumbnail = null;
          resolvedThumbnailSource = "none";
          resolvedThumbnailBuilderData = null;
        } else {
          const generatedSrc = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
            builderData.svgString,
          )}`;
          resolvedThumbnail = {
            src: builderData.rasterPreviewUrl || generatedSrc,
            svgSrc: generatedSrc,
            alt: "Generated thumbnail preview",
          };
          resolvedThumbnailSource = "generated";
          resolvedThumbnailBuilderData = builderData;
          resolvedThumbnailTempFilename = null;
        }
      }

      // If thumbnail comes from the SVG builder, upload it first so we persist a Bunny URL.
      if (resolvedThumbnail?.svgSrc && !resolvedThumbnailTempFilename) {
        setThumbnailUploading(true);
        try {
          const generatedTempFilename = `temp_${Date.now()}_${Math.floor(
            Math.random() * 10000,
          )}.png`;

          const generatedFile = await svgDataUrlToPngFile(
            resolvedThumbnail.svgSrc,
            generatedTempFilename,
          );

          if (!generatedFile) {
            throw new Error("Failed to prepare generated thumbnail.");
          }

          const response = await uploadDreamcastThumbnailFile(generatedFile, {
            destination: "medias",
          });

          const uploadedThumbnailUrl = response?.data?.thumbnailUrl;
          const uploadedTempFilename =
            response?.data?.tempFilename || generatedTempFilename;

          if (!uploadedThumbnailUrl || !uploadedTempFilename) {
            throw new Error("Failed to upload generated thumbnail.");
          }

          resolvedThumbnail = {
            ...resolvedThumbnail,
            src: uploadedThumbnailUrl,
          };
          resolvedThumbnailTempFilename = uploadedTempFilename;
          setThumbnail(resolvedThumbnail);
          setThumbnailTempFilename(uploadedTempFilename);
        } finally {
          setThumbnailUploading(false);
        }
      }

      const payload = toApiAudioPayload({
        thumbnail: resolvedThumbnail,
        thumbnailSource: resolvedThumbnailSource,
        thumbnailBuilderData: resolvedThumbnailBuilderData,
        audioDetails,
        files,
        archivedFiles,
        publishState,
        contentSettings,
        disclosures: mediaDisclosures,
        speakers,
        isDraft: mode === "draft",
        defaultDigitalDeliveryEnabled,
      });

      let targetId = editingAudioId;
      // Changing the SKU renames the MediaKey (the record id); the old id no
      // longer exists once the update returns.
      let previousId = null;

      if (editingAudioId) {
        const updated = await resolvedApi.update(editingAudioId, payload);
        const savedId = updated?.data?.mediaItemId;
        if (savedId && String(savedId) !== String(editingAudioId)) {
          previousId = editingAudioId;
          targetId = savedId;
          setEditingAudioId(savedId);
        }
      } else {
        const created = await resolvedApi.create(payload);
        targetId = created?.data?.mediaItemId || null;
        // The record now exists. If a later step (file moves) fails, the form
        // stays open — retrying must update this record, not create a
        // duplicate that collides on the SKU.
        if (targetId) {
          setEditingAudioId(targetId);
          createdRecordId = targetId;
        }
      }

      const isSavedRecordId = (id) =>
        String(id) === String(targetId) ||
        (previousId !== null && String(id) === String(previousId));
      const withoutPreviousId = (items) =>
        previousId === null
          ? items
          : items.filter(
              (item) => String(item.mediaItemId) !== String(previousId),
            );

      const currentListItem = audios.find((item) =>
        isSavedRecordId(item.mediaItemId),
      );
      const optimisticItem = buildOptimisticMediaListItem(
        currentListItem,
        payload,
        targetId,
      );
      setAudios((list) => {
        const current = withoutPreviousId(list);
        const hasExistingItem = current.some(
          (item) => String(item.mediaItemId) === String(targetId),
        );

        if (hasExistingItem) {
          return current.map((item) =>
            String(item.mediaItemId) === String(targetId)
              ? optimisticItem
              : item,
          );
        }

        return [optimisticItem, ...current];
      });

      // Queue any pending file operations for the background
      if (targetId) {
        const ops = {};
        let activeFileOp = null;
        const resolveStoredFilename = (file) =>
          file?.tempFilename || file?.name || null;

        const currentActiveFile =
          Array.isArray(filesRef.current) && filesRef.current.length > 0
            ? filesRef.current[0]
            : null;
        const currentActiveFilename = resolveStoredFilename(currentActiveFile);
        const startingActiveFilename = startingActiveFilenameRef.current;
        const isRestoreFromPersistedArchive =
          Boolean(restoredMediaFile) &&
          Boolean(editingAudioId) &&
          !restoredMediaFile?.newActive?.tempFilename;
        const isEditSwapToArchivedFile =
          Boolean(editingAudioId) &&
          Boolean(startingActiveFilename) &&
          Boolean(currentActiveFilename) &&
          currentActiveFilename !== startingActiveFilename &&
          !currentActiveFile.tempFilename;

        // Explicit edit swap flow:
        // starting active -> archived_files, selected archived -> active
        if (isEditSwapToArchivedFile) {
          activeFileOp = {
            type: "restore",
            mediaId: targetId,
            archivedFilename: currentActiveFilename,
            currentActiveFilename: startingActiveFilename,
          };
          setRestoredMediaFile(null);
          startingActiveFilenameRef.current = currentActiveFilename;
        } else if (isRestoreFromPersistedArchive) {
          // Fallback restore detection path
          const restoredFilename = resolveStoredFilename(
            restoredMediaFile.newActive,
          );
          const previousActiveFilename = resolveStoredFilename(
            restoredMediaFile.oldActive,
          );
          activeFileOp = {
            type: "restore",
            mediaId: targetId,
            archivedFilename: restoredFilename,
            currentActiveFilename: previousActiveFilename,
          };
          setRestoredMediaFile(null);
          startingActiveFilenameRef.current = restoredFilename;
        } else {
          // NEW FLOW: Handle active media file (single active file per media)
          // Find the first file with a tempFilename (most recently uploaded)
          const newActiveFile = filesRef.current.find((f) => f.tempFilename);
          if (newActiveFile) {
            let oldActiveFilename = null;
            // In edit mode, archive the active file we started with.
            if (editingAudioId && startingActiveFilename) {
              oldActiveFilename = startingActiveFilename;
            } else {
              // Fallback for non-edit flows: first persisted archived file.
              const persistedArchivedFiles = (
                archivedFilesRef?.current || archivedFiles
              ).filter((f) => f.previewUrl && !f.tempFilename);

              if (persistedArchivedFiles.length > 0) {
                oldActiveFilename = resolveStoredFilename(
                  persistedArchivedFiles[0],
                );
              }
            }

            activeFileOp = {
              type: "move",
              mediaId: targetId,
              tempFilename: newActiveFile.tempFilename,
              oldActiveFilename, // File to move to archive (if any)
            };
            startingActiveFilenameRef.current =
              resolveStoredFilename(newActiveFile);
          }
        }

        // Move any temp files currently marked as archived into archived_files.
        const archivedTempFiles = (archivedFilesRef.current || [])
          .filter((f) => {
            if (!f?.tempFilename) return false;
            const previewUrl = String(f.previewUrl || "");
            const hasTempPreview =
              previewUrl.includes("/medias/temp/") ||
              previewUrl.includes("/profiles/temp/");
            return Boolean(f.localPreviewUrl) || hasTempPreview;
          })
          .map((f) => f.tempFilename);

        if (archivedTempFiles.length > 0) {
          ops.archivedFiles = {
            mediaId: targetId,
            filenames: Array.from(new Set(archivedTempFiles)),
          };
        }

        if (resolvedThumbnailTempFilename) {
          ops.image = {
            mediaId: targetId,
            tempFilename: resolvedThumbnailTempFilename,
            oldUrl: thumbnailPrevUrl || undefined,
          };
        }

        if (removedArchivedFiles.length > 0) {
          ops.deletedFiles = {
            mediaId: targetId,
            fileUrls: removedArchivedFiles
              .map((file) => file?.previewUrl)
              .filter(Boolean),
          };
        }

        // Execute file operations immediately so they are not lost if form state resets.
        const fileTasks = [];
        if (activeFileOp) {
          fileTasks.push(
            activeFileOp.type === "restore"
              ? {
                  label: "the restored media file",
                  run: () =>
                    restoreMediaArchivedFile(
                      activeFileOp.mediaId,
                      activeFileOp.archivedFilename,
                      activeFileOp.currentActiveFilename,
                    ),
                }
              : {
                  label: "the media file",
                  run: () =>
                    moveMediaActiveFile(
                      activeFileOp.mediaId,
                      activeFileOp.tempFilename,
                      activeFileOp.oldActiveFilename,
                    ),
                },
          );
        }
        if (ops.archivedFiles) {
          fileTasks.push({
            label: "archived files",
            run: () =>
              moveMediaTempArchivedFiles(
                ops.archivedFiles.mediaId,
                ops.archivedFiles.filenames,
              ),
          });
        }
        if (ops.image) {
          fileTasks.push({
            label: "the thumbnail",
            run: () =>
              moveMediaTempImage(
                ops.image.mediaId,
                ops.image.tempFilename,
                ops.image.oldUrl,
              ),
          });
        }
        if (
          ops.deletedFiles &&
          Array.isArray(ops.deletedFiles.fileUrls) &&
          ops.deletedFiles.fileUrls.length > 0
        ) {
          fileTasks.push({
            label: "file deletions",
            run: () =>
              deleteMediaFiles(
                ops.deletedFiles.mediaId,
                ops.deletedFiles.fileUrls,
              ),
          });
        }
        if (fileTasks.length > 0) {
          await runSaveFileOperations(fileTasks);
        }
      }

      try {
        await loadAudios();
      } catch (error) {
        console.error("[Media] Failed to refresh audios after save:", error);
      }

      // Keep the saved values visible even if the API list is briefly stale.
      setAudios((list) => {
        const current = withoutPreviousId(list);
        const hasExistingItem = current.some(
          (item) => String(item.mediaItemId) === String(targetId),
        );

        if (hasExistingItem) {
          return current.map((item) => {
            if (String(item.mediaItemId) !== String(targetId)) {
              return item;
            }

            const optimistic = buildOptimisticMediaListItem(
              item,
              payload,
              targetId,
            );
            // The thumbnail was moved out of temp storage during this save;
            // the freshly reloaded item already points at its final URL.
            return isTempUploadUrl(optimistic.thumbnailPath) &&
              item.thumbnailPath &&
              !isTempUploadUrl(item.thumbnailPath)
              ? { ...optimistic, thumbnailPath: item.thumbnailPath }
              : optimistic;
          });
        }

        return targetId ? [optimisticItem, ...current] : current;
      });

      const speakerFormState = location.state?.speakerFormState;
      const speakerReturnPath = location.state?.speakerReturnPath;
      if (
        editingAudioId &&
        speakerFormState &&
        typeof speakerFormState === "object" &&
        speakerReturnPath
      ) {
        const existingSpeakerAudios = Array.isArray(
          speakerFormState.speakerAudios,
        )
          ? speakerFormState.speakerAudios
          : [];
        const updatedSpeakerAudio = {
          ...existingSpeakerAudios.find((audio) => isSavedRecordId(audio.id)),
          id: String(targetId),
          thumbnail: optimisticItem.thumbnailPath || "",
          title: optimisticItem.title || "Untitled Audio",
          date: optimisticItem.date || "",
          speaker: optimisticItem.contributorName || "",
          episodePart: payload.detailState?.episodePart || "",
        };
        const updatedSpeakerAudios = existingSpeakerAudios.some((audio) =>
          isSavedRecordId(audio.id),
        )
          ? existingSpeakerAudios.map((audio) =>
              isSavedRecordId(audio.id) ? updatedSpeakerAudio : audio,
            )
          : [...existingSpeakerAudios, updatedSpeakerAudio];

        history.push({
          pathname: speakerReturnPath,
          state: {
            fromMediaSelection: true,
            speakerFormState: {
              ...speakerFormState,
              speakerAudios: updatedSpeakerAudios,
            },
          },
        });
        return;
      }

      // Optimistic UI: close form immediately
      setIsCreateView(false);
      resetCreateForm();
      setApiError("");

      if (onCreateComplete) {
        onCreateComplete({ mode, item: null });
      }
    } catch (error) {
      // Detect SKU duplicate error from API
      const isSkuExists =
        error?.data?.error?.code === "SKU_EXISTS" && error?.status === 409;

      if (isSkuExists) {
        setSkuError(error.message);
        setSkuDuplicateError(true);
        setShowSkuDuplicateModal(true);
      } else {
        setSkuError("");
        setSkuDuplicateError(false);
      }

      const recordSaved =
        !isSkuExists && (createdRecordId || error?.failedFileOps);
      setApiError(
        recordSaved
          ? `The media details were saved, but ${
              error.message || "its files could not be saved"
            }. Click Save again to finish.`
          : error.message || "Failed to save media.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const doCancelCreate = () => {
    setPendingCancel(false);
    leaveConfirmedRef.current = true;

    const speakerFormState = location.state?.speakerFormState;
    const speakerReturnPath = location.state?.speakerReturnPath;
    if (
      editingAudioId &&
      speakerFormState &&
      typeof speakerFormState === "object" &&
      speakerReturnPath
    ) {
      history.push({
        pathname: speakerReturnPath,
        state: {
          fromMediaSelection: true,
          speakerFormState,
        },
      });
      return;
    }

    setIsCreateView(false);
    resetCreateForm();
    if (onCreateCancel) {
      onCreateCancel();
    }
  };

  // Guard Cancel while the form has unsaved changes (see pendingCancel modal).
  const handleCancelCreate = () => {
    if (isFormDirty || isUploadInProgress) {
      setPendingCancel(true);
      return;
    }
    doCancelCreate();
  };

  const handleArchiveAudio = async (id) => {
    const currentItem = audios.find((item) => item.mediaItemId === id);
    if (!currentItem) {
      return;
    }

    const workflowStatus =
      currentItem.workflowStatus === "archived" ? "published" : "archived";
    const displayStatus = ARCHIVE_DISPLAY_STATUS[workflowStatus];

    setArchivingAudioId(id);
    try {
      setApiError("");
      await resolvedApi.setArchiveState(id, workflowStatus, displayStatus);
      await loadAudios();
    } catch (error) {
      setApiError(error.message || "Failed to update archive state.");
    } finally {
      setArchivingAudioId(null);
    }
  };

  const handleDuplicateAudio = async (id) => {
    try {
      setApiError("");
      const sourceItem = audios.find(
        (item) => String(item.mediaItemId) === String(id),
      );
      if (!sourceItem) return;

      setDuplicationInProgress((current) => {
        const next = new Set(current);
        next.add(String(id));
        return next;
      });

      const duplicateResponse = await resolvedApi.duplicate(id);
      const duplicatedItem = duplicateResponse?.data || duplicateResponse;
      const duplicatedId = duplicatedItem?.mediaItemId;

      if (duplicatedId == null) {
        throw new Error("Duplicate response did not include a media ID.");
      }

      const refreshedAudios = await loadAudios();
      const copyIndex = refreshedAudios.findIndex(
        (item) => String(item.mediaItemId) === String(duplicatedId),
      );
      if (copyIndex < 0) {
        throw new Error("Duplicated media was not returned by the media list.");
      }

      const copy = refreshedAudios[copyIndex];
      const audiosWithoutCopy = refreshedAudios.filter(
        (item) => String(item.mediaItemId) !== String(duplicatedId),
      );
      const sourceIndex = audiosWithoutCopy.findIndex(
        (item) => String(item.mediaItemId) === String(id),
      );
      const insertIndex = sourceIndex < 0 ? 0 : sourceIndex;
      audiosWithoutCopy.splice(insertIndex, 0, {
        ...copy,
        title: copy.title || `${sourceItem.title} (Copy)`,
      });
      setAudios(audiosWithoutCopy);

      await openEditView(duplicatedId);
    } catch (error) {
      setApiError(error.message || "Failed to duplicate media.");
      await loadAudios();
    } finally {
      setDuplicationInProgress((current) => {
        const next = new Set(current);
        next.delete(String(id));
        return next;
      });
    }
  };

  // Process pending file operations in the background
  useEffect(() => {
    if (!pendingFileOps) {
      return;
    }

    const processPendingOps = async () => {
      try {
        const promises = [];

        if (pendingFileOps.archivedFiles) {
          promises.push(
            moveMediaTempArchivedFiles(
              pendingFileOps.archivedFiles.mediaId,
              pendingFileOps.archivedFiles.filenames,
            ),
          );
        }

        if (pendingFileOps.image) {
          promises.push(
            moveMediaTempImage(
              pendingFileOps.image.mediaId,
              pendingFileOps.image.tempFilename,
              pendingFileOps.image.oldUrl,
            ),
          );
        }

        if (
          pendingFileOps.deletedFiles &&
          Array.isArray(pendingFileOps.deletedFiles.fileUrls) &&
          pendingFileOps.deletedFiles.fileUrls.length > 0
        ) {
          promises.push(
            deleteMediaFiles(
              pendingFileOps.deletedFiles.mediaId,
              pendingFileOps.deletedFiles.fileUrls,
            ),
          );
        }

        if (promises.length > 0) {
          await Promise.all(promises);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[Media] Failed to move files:", error);
        setApiError(
          error.message || "Files moved to temporary location. Please refresh.",
        );
      } finally {
        setPendingFileOps(null);
      }
    };

    processPendingOps();
  }, [pendingFileOps]);

  // Process pending active file operations in the background
  useEffect(() => {
    if (!pendingActiveFileOp) {
      return;
    }

    const processPendingActiveFileOp = async () => {
      try {
        if (pendingActiveFileOp.type === "restore") {
          // Restore operation: move archived to active, current to archive
          await restoreMediaArchivedFile(
            pendingActiveFileOp.mediaId,
            pendingActiveFileOp.archivedFilename,
            pendingActiveFileOp.currentActiveFilename,
          );
        } else {
          // Move operation: move temp to active, current to archive
          await moveMediaActiveFile(
            pendingActiveFileOp.mediaId,
            pendingActiveFileOp.tempFilename,
            pendingActiveFileOp.oldActiveFilename,
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[Media] Failed to process file operation:", error);
        setApiError(
          error.message ||
            "Failed to finalize media file. Please refresh to retry.",
        );
      } finally {
        setPendingActiveFileOp(null);
      }
    };

    processPendingActiveFileOp();
  }, [pendingActiveFileOp]);

  // Detect when user unarchives a file (restore operation)
  useEffect(() => {
    if (!activeMediaFile || files.length === 0) {
      setRestoredMediaFile(null);
      return;
    }

    const currentActive = files[0];

    // Check if the active file changed and the old one is now archived
    if (activeMediaFile.id !== currentActive.id) {
      const oldFileIsArchived = archivedFiles.some(
        (f) => f.id === activeMediaFile.id,
      );

      if (oldFileIsArchived) {
        // This is a restore operation: old active → archive, new → active
        setRestoredMediaFile({
          newActive: currentActive,
          oldActive: activeMediaFile,
        });
      }
    }
  }, [files, archivedFiles, activeMediaFile]);

  const openEditView = React.useCallback(
    async (id) => {
      const normalizedId = String(id);
      const normalizedInitialEditingId =
        initialEditingAudioId == null ? null : String(initialEditingAudioId);
      let target = audios.find(
        (item) => String(item.mediaItemId) === normalizedId,
      );
      let bunnyFiles = null;
      const shouldUseInitialEditingData =
        preferInitialEditingData &&
        normalizedInitialEditingId != null &&
        normalizedId === normalizedInitialEditingId &&
        target;

      if (shouldUseInitialEditingData) {
        bunnyFiles = Array.isArray(target.files) ? target.files : [];
      } else {
        try {
          setApiError("");

          const [detailResponse, filesResponse] = await Promise.allSettled([
            resolvedApi.fetchById(id),
            resolvedApi.fetchFilesById
              ? resolvedApi.fetchFilesById(id)
              : Promise.resolve({ data: [] }),
          ]);

          if (detailResponse.status === "fulfilled") {
            target = detailResponse.value?.data || target;
          } else {
            throw detailResponse.reason;
          }

          if (filesResponse.status === "fulfilled") {
            bunnyFiles = Array.isArray(filesResponse.value?.data)
              ? filesResponse.value.data
              : [];
          }
        } catch (error) {
          setApiError(error.message || "Failed to load media details.");
        }
      }

      if (!target) return;

      setEditingAudioId(target.mediaItemId);
      setEditingWasDraft(Boolean(target.isDraft));

      const audioDetailsWithSync = {
        ...DEFAULT_AUDIO_DETAILS,
        ...target.detailState,
      };

      // Auto-sync Release Date with Publish On date if year is empty or just a year string
      const publishOnDate = target.publishState?.publishOn;
      const currentReleaseDate = target.detailState?.year;
      const isYearOnly =
        currentReleaseDate && /^\d{4}$/.test(currentReleaseDate.trim());
      const isEmpty = !currentReleaseDate;

      // If year is empty or just a year string (not ISO), always sync with publishOn
      if ((isYearOnly || isEmpty) && publishOnDate) {
        audioDetailsWithSync.year = publishOnDate;
      }

      setAudioDetails(audioDetailsWithSync);

      setThumbnail(
        target.thumbnailPath
          ? { src: target.thumbnailPath, alt: `${target.title} thumbnail` }
          : null,
      );
      setThumbnailSource(
        target.thumbnailSource || (target.thumbnailPath ? "custom" : "none"),
      );
      setThumbnailTempFilename(null);
      setThumbnailPrevUrl(target.thumbnailPath || "");
      setThumbnailBuilderData(target.thumbnailBuilderData);
      setThumbnailBuilderDraft(null);
      setThumbnailBuilderOpen(target.thumbnailSource === "generated");

      const nextFiles = Array.isArray(bunnyFiles)
        ? bunnyFiles
        : Array.isArray(target.files)
        ? target.files
        : [];

      // Merge CR numbers and duration from detailState and media level into the active file (first file)
      if (nextFiles.length > 0 && target.detailState) {
        nextFiles[0] = {
          ...nextFiles[0],
          naCrNumber: target.detailState.naCrNumber || nextFiles[0]?.naCrNumber,
          anzCrNumber:
            target.detailState.anzCrNumber || nextFiles[0]?.anzCrNumber,
          duration: target.durationSeconds || nextFiles[0]?.duration,
        };
      }

      filesRef.current = nextFiles;
      setFiles(nextFiles);
      setArchivedFiles(
        Array.isArray(target.archivedFiles) ? target.archivedFiles : [],
      );
      setRemovedArchivedFiles([]);

      setPublishState({
        ...createDefaultPublishState(defaultDigitalDeliveryEnabled),
        ...target.publishState,
      });
      setContentSettings(
        normalizeContentSettingsFromApi(target.contentSettings, {
          ...contentSettingsConfig,
          // Inject processed option lists so the component has the full display arrays
          _processedLists: basePermissionState,
        }) || basePermissionState,
      );
      setMediaDisclosures(withDisclosureAssignmentIds(target.disclosures));
      setSpeakers(Array.isArray(target.speakers) ? target.speakers : []);

      setIsCreateView(true);
    },
    [
      audios,
      basePermissionState,
      contentSettingsConfig,
      defaultDigitalDeliveryEnabled,
      initialEditingAudioId,
      preferInitialEditingData,
      resolvedApi,
    ],
  );

  const handleEditAudio = React.useCallback(
    async (id) => {
      if (hasPendingFileOps(id)) {
        setApiError(
          "This media is still processing files. Please wait before editing.",
        );
        setEditAttemptedDuringMediaIds((current) =>
          new Set(current).add(String(id)),
        );
        return;
      }

      setLoadingAudioId(id);
      try {
        await openEditView(id);
      } finally {
        setLoadingAudioId(null);
      }
    },
    [openEditView, pendingFileOps],
  );

  React.useEffect(() => {
    if (
      hasAppliedInitialEdit ||
      !initialEditingAudioId ||
      isCreateView ||
      cameFromSpeakerSelection
    ) {
      return;
    }

    // Enter edit view immediately to avoid showing an intermediate list page.
    setIsCreateView(true);
    setLoadingAudioId(initialEditingAudioId);
    openEditView(initialEditingAudioId).finally(() => {
      setLoadingAudioId(null);
    });
    setHasAppliedInitialEdit(true);
  }, [
    cameFromSpeakerSelection,
    openEditView,
    hasAppliedInitialEdit,
    initialEditingAudioId,
    isCreateView,
    preferInitialEditingData,
  ]);

  // Clear edit attempt tracking when file operations complete
  useEffect(() => {
    if (!pendingFileOps && editAttemptedDuringMediaIds.size > 0) {
      const mediaId = Array.from(editAttemptedDuringMediaIds)[0];
      if (mediaId) {
        handleEditAudio(Number(mediaId));
      }
      setEditAttemptedDuringMediaIds(new Set());
    }
  }, [pendingFileOps, editAttemptedDuringMediaIds, handleEditAudio]);

  const handleSpeakerAdd = () => {
    const dreamcastRootMatch = location.pathname.match(
      /^(.*\/dreamcast)(?:\/.*)?$/,
    );
    const baseDreamCastPath = dreamcastRootMatch
      ? dreamcastRootMatch[1]
      : "/dreamcast";

    const isDigitalDeliveryCreateRoute = /\/digital-delivery\/add(?:\/|$)/.test(
      location.pathname,
    );
    const digitalDeliveryEditMatch = location.pathname.match(
      /^(.*\/dreamcast\/digital-delivery\/edit\/[^/]+)(?:\/.*)?$/,
    );
    const isDigitalDeliveryEditRoute = Boolean(digitalDeliveryEditMatch);

    const returnPath = isDigitalDeliveryCreateRoute
      ? `${baseDreamCastPath}/digital-delivery/add`
      : isDigitalDeliveryEditRoute
      ? digitalDeliveryEditMatch[1]
      : `${baseDreamCastPath}/media`;

    const selectionSource = isDigitalDeliveryCreateRoute
      ? "digital-delivery-add"
      : isDigitalDeliveryEditRoute
      ? "digital-delivery-edit"
      : "audios";

    const mediaFormState = {
      editingAudioId,
      editingWasDraft,
      audioDetails,
      thumbnail,
      thumbnailSource,
      thumbnailTempFilename,
      thumbnailPrevUrl,
      thumbnailBuilderData,
      thumbnailBuilderDraft,
      thumbnailBuilderOpen,
      files,
      archivedFiles,
      removedArchivedFiles,
      publishState,
      contentSettings,
      disclosures: mediaDisclosures,
      speakers,
      activeMediaFile,
      restoredMediaFile,
      startingActiveFilename: startingActiveFilenameRef.current,
      apiError,
      historyBaseline: historyBaseline || buildFormSnapshot(),
    };

    history.push({
      pathname: `${baseDreamCastPath || "/dreamcast"}/speaker-list`,
      search: `?mode=selection&source=${selectionSource}`,
      state: {
        preselectedSpeakerIds: speakers.map((speaker) => speaker.id),
        speakerSelectionReturnPath: returnPath,
        mediaFormState,
      },
    });
  };

  const handleMediaSelectionToggle = (mediaId) => {
    const id = String(mediaId);
    setSelectedMediaIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );
  };

  const returnToSpeakerWithSelectedMedia = (mediaIds) => {
    const mediaLookup = new Set((mediaIds || []).map(String));
    const selectedMediaAudios = selectionAudios
      .filter((item) => mediaLookup.has(String(item.mediaItemId)))
      .map((item) => ({
        id: String(item.mediaItemId),
        thumbnail: item.thumbnailPath || "",
        title: item.title || "Untitled Audio",
        date: item.date || "",
        speaker: item.contributorName || "",
        episodePart: item.episodePart || "",
      }));
    const speakerFormState =
      location.state?.speakerFormState &&
      typeof location.state.speakerFormState === "object"
        ? location.state.speakerFormState
        : null;

    history.push({
      pathname:
        location.state?.mediaSelectionReturnPath || "/dreamcast/speaker-list",
      state: {
        fromMediaSelection: true,
        selectedMediaAudios,
        ...(speakerFormState ? { speakerFormState } : {}),
      },
    });
  };

  return (
    <div className={styles.page} ref={pageRef}>
      <Prompt
        when={isCreateView && isUploadInProgress}
        message={() => {
          if (leaveConfirmedRef.current) {
            leaveConfirmedRef.current = false;
            return true;
          }
          return "A file is still uploading. If you leave this page, the upload will be cancelled. Leave anyway?";
        }}
      />
      <ControlsHeader
        title={
          isSpeakerMediaSelection ? "DreamCast - Speaker Media" : headerTitle
        }
      />

      {isLoadingList && audios.length === 0 ? (
        <div className={styles.initialLoaderOverlay}>
          <Loader color="#415364" size={85} />
        </div>
      ) : !isCreateView ? (
        <section className={styles.listLayout}>
          {apiError ? (
            <div className={styles.errorBanner}>{apiError}</div>
          ) : null}
          <div className={styles.toolbar}>
            <SearchBar
              placeholderText="Search Media"
              value={searchQuery}
              onInput={setSearchQuery}
              minChar={0}
              searchDelay={0}
              noResults={searchBarNoResults}
            />
            {isSelectionMode ? (
              <span className={styles.selectedCount}>
                Selected Media: {selectedMediaIds.length}
              </span>
            ) : (
              <Button
                className={styles.addButton}
                onClick={() => setIsCreateView(true)}
              >
                {addButtonLabel}
              </Button>
            )}
            <SortModal
              options={SORT_OPTIONS}
              selected={sortBy}
              onChange={setSortBy}
              container={pageRef}
            />
            {isSelectionMode ? (
              <div className={styles.selectionActions}>
                <Button
                  className={styles.addSelectedButton}
                  onClick={() =>
                    returnToSpeakerWithSelectedMedia(selectedMediaIds)
                  }
                >
                  +Add
                </Button>
                <Button
                  className={styles.cancelSelectButton}
                  onClick={() =>
                    returnToSpeakerWithSelectedMedia(
                      location.state?.preselectedMediaIds || [],
                    )
                  }
                >
                  Cancel
                </Button>
                <Button
                  className={styles.selectAllButton}
                  onClick={() =>
                    setSelectedMediaIds(
                      selectionAudios.map((item) => String(item.mediaItemId)),
                    )
                  }
                >
                  Select All
                </Button>
                <Button
                  className={styles.unselectAllButton}
                  onClick={() => setSelectedMediaIds([])}
                >
                  Unselect All
                </Button>
              </div>
            ) : null}
          </div>

          <div className={styles.listBody}>
            <FilterColumn
              className={styles.filterColumn}
              sections={filterSections}
              selected={filters}
              onChange={setFilters}
            />

            <div
              className={`${styles.listContent} ${
                paginatedAudios.length === 0 ? styles.noResults : ""
              }`.trim()}
            >
              <div className={styles.columnHeaders}>
                <span className={styles.colImage}>Image</span>
                <div className={styles.headerInfoCluster}>
                  <span className={styles.colInfo}>Title/Info</span>
                  <div className={styles.headerRightColumn}>
                    <span className={styles.colSku}>SKU</span>
                    <span className={styles.colType}>Type</span>
                    <span className={styles.colStatus}>Status</span>
                  </div>
                </div>
              </div>

              <div className={styles.listItems}>
                {filtersNoResults ? (
                  <p className={styles.emptyState}>No results found.</p>
                ) : null}
                {paginatedAudios.map((item) => {
                  const normalizedItem = normalizeMediaListItem(
                    item,
                    contentSettingsConfig,
                  );
                  const isEditLoading =
                    loadingAudioId === item.mediaItemId ||
                    editAttemptedDuringMediaIds.has(String(item.mediaItemId));
                  const isDuplicateLoading = duplicationInProgress.has(
                    String(item.mediaItemId),
                  );
                  const isArchiveLoading =
                    archivingAudioId === item.mediaItemId;
                  const isItemLoading =
                    isEditLoading || isDuplicateLoading || isArchiveLoading;

                  return (
                    <AudioListItem
                      key={item.mediaItemId}
                      id={item.mediaItemId}
                      thumbnail={normalizedItem.thumbnailPath}
                      title={normalizedItem.title}
                      date={normalizedItem.date}
                      speaker={normalizedItem.contributorName}
                      sku={normalizedItem.sku}
                      type={normalizedItem.mediaTypeCode}
                      status={normalizedItem.displayStatus}
                      isDraft={normalizedItem.isDraft}
                      showMembershipPermissions
                      tags={normalizedItem.tags}
                      pinPermissions={normalizedItem.pinPermissions}
                      membershipPermissions={
                        normalizedItem.membershipPermissions
                      }
                      layoutVariant="audios"
                      selectable={isSelectionMode}
                      selected={selectedMediaIds.includes(
                        String(item.mediaItemId),
                      )}
                      onSelect={handleMediaSelectionToggle}
                      onDuplicate={
                        isSelectionMode
                          ? undefined
                          : () => handleDuplicateAudio(item.mediaItemId)
                      }
                      onArchive={
                        isSelectionMode
                          ? undefined
                          : () => handleArchiveAudio(item.mediaItemId)
                      }
                      onEdit={
                        isSelectionMode
                          ? undefined
                          : () => handleEditAudio(item.mediaItemId)
                      }
                      disabled={isItemLoading}
                      isEditLoading={isEditLoading}
                      isDuplicateLoading={isDuplicateLoading}
                      isArchiveLoading={isArchiveLoading}
                    />
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className={styles.paginationWrapper}>
                  <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    ariaLabel="Media items pagination"
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      ) : (
        <section className={styles.createLayout}>
          {showHistory && historyBaseline && (
            <HistoryOverlay
              baseline={historyBaseline}
              current={buildFormSnapshot()}
              fieldDefs={MEDIA_HISTORY_FIELD_DEFS}
              onRestoreField={handleRestoreField}
              onClose={() => setShowHistory(false)}
              onUndoAll={() => {
                setShowHistory(false);
                setPendingUndo(true);
              }}
            />
          )}

          <Modal
            isOpen={pendingUndo}
            title="Undo all changes?"
            onClose={() => setPendingUndo(false)}
            className={styles.lightModeModal}
          >
            <div className={styles.undoModalBody}>
              <p>
                This reverts every field back to the last saved version. Any
                edits you have made since then will be discarded and cannot be
                recovered.
              </p>
              <div className={styles.undoModalActions}>
                <Button onClick={() => setPendingUndo(false)}>
                  Keep Editing
                </Button>
                <Button purpose={ButtonPurpose.delete} onClick={confirmUndo}>
                  Undo Changes
                </Button>
              </div>
            </div>
          </Modal>

          <Modal
            isOpen={pendingCancel}
            title="Leave the editor?"
            onClose={() => setPendingCancel(false)}
            className={styles.lightModeModal}
          >
            <div className={styles.undoModalBody}>
              {isUploadInProgress && (
                <p>
                  A file is still uploading. If you leave now, the upload will
                  be cancelled.
                </p>
              )}
              <p>
                You have unsaved changes. If you leave now, they will be
                discarded and cannot be recovered.
              </p>
              <p>
                Click <strong>Save</strong> to keep your changes.
              </p>
              <div className={styles.undoModalActions}>
                <Button onClick={() => setPendingCancel(false)}>
                  Keep Editing
                </Button>
                <Button purpose={ButtonPurpose.delete} onClick={doCancelCreate}>
                  Leave
                </Button>
              </div>
            </div>
          </Modal>

          <Modal
            isOpen={showSkuDuplicateModal}
            title="Existing SKU"
            onClose={() => setShowSkuDuplicateModal(false)}
            className={styles.lightModeModal}
          >
            <div className={styles.undoModalBody}>
              <p>
                This SKU already exists and has been used for another piece of
                media. Please use a unique SKU for this media.
              </p>
              <div className={styles.undoModalActions}>
                <Button onClick={() => setShowSkuDuplicateModal(false)}>
                  Continue Editing
                </Button>
              </div>
            </div>
          </Modal>

          <div className={styles.actionBarRow}>
            <FormActionBar
              onSave={() => createFromCurrentForm("save")}
              onSaveDraft={() => createFromCurrentForm("draft")}
              onCancel={handleCancelCreate}
              isDraft={editingWasDraft}
              isSaving={isSaving}
              disabled={isSaving}
              isDirty={isFormDirty}
              onShowHistory={() => setShowHistory(true)}
              embedded
            />
          </div>

          <div className={styles.formColumns}>
            <div className={styles.leftColumn}>
              <AudioDetailsForm
                {...audioDetails}
                publishOn={publishState?.publishOn}
                mediaTypeOptions={mediaTypeOptions}
                contentTypeOptions={mediaTemplates.map(
                  (template) => template.label,
                )}
                mediaTemplates={mediaTemplates}
                sourceOptions={mediaSourceOptions}
                errors={attemptedSave ? requiredFieldErrors : {}}
                skuDuplicateError={skuDuplicateError}
                onChange={(field, value, nextValues) => {
                  // When Media Type is set to "Music", automatically set Content Type to "Music"
                  if (field === "mediaType" && value === "Music") {
                    nextValues.contentType = "Music";
                  }
                  setAudioDetails(nextValues);
                  // Clear SKU duplicate error when user edits SKU
                  if (field === "sku") {
                    setSkuDuplicateError(false);
                  }
                  // Clear error for this field when user starts typing
                  if (requiredFieldErrors[field]) {
                    setRequiredFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next[field];
                      return next;
                    });
                  }
                  if (
                    field === "title" ||
                    field === "creatorSpeaker" ||
                    field === "sku"
                  ) {
                    setThumbnailBuilderData((current) => ({
                      ...(current || {}),
                      details: {
                        ...(current?.details || {}),
                        title:
                          field === "title"
                            ? value
                            : current?.details?.title || "",
                        creatorSpeaker:
                          field === "creatorSpeaker"
                            ? value
                            : current?.details?.creatorSpeaker || "",
                        topic:
                          field === "topic"
                            ? value
                            : current?.details?.topic ||
                              audioDetails.topic ||
                              "",
                        sku:
                          field === "sku"
                            ? value
                            : current?.details?.sku || audioDetails.sku || "",
                        naCrNumber:
                          field === "naCrNumber"
                            ? value
                            : current?.details?.naCrNumber ||
                              audioDetails.naCrNumber ||
                              "",
                        anzCrNumber:
                          field === "anzCrNumber"
                            ? value
                            : current?.details?.anzCrNumber ||
                              audioDetails.anzCrNumber ||
                              "",
                      },
                    }));
                  }
                }}
              />

              {!thumbnailBuilderOpen && (
                <ThumbnailPreview
                  thumbnail={thumbnail}
                  onAddExisting={async (file) => {
                    if (!file) return;
                    setThumbnailSource("custom");
                    const localUrl = URL.createObjectURL(file);
                    setThumbnail({
                      src: localUrl,
                      alt: file.name || "Thumbnail",
                    });
                    setThumbnailTempFilename(null);
                    setThumbnailUploading(true);
                    try {
                      const response = await uploadDreamcastThumbnailFile(
                        file,
                        {
                          destination: "medias",
                        },
                      );
                      if (response?.data?.thumbnailUrl) {
                        setThumbnail({
                          src: response.data.thumbnailUrl,
                          alt: file.name || "Thumbnail",
                        });
                        setThumbnailTempFilename(
                          response.data.tempFilename || null,
                        );
                      }
                    } catch {
                      // Keep local blob URL if CDN upload fails
                    } finally {
                      setThumbnailUploading(false);
                    }
                  }}
                  onDrop={async (file) => {
                    setThumbnailSource("custom");
                    const localUrl = URL.createObjectURL(file);
                    setThumbnail({
                      src: localUrl,
                      alt: file.name || "Thumbnail",
                    });
                    setThumbnailTempFilename(null);
                    setThumbnailUploading(true);
                    try {
                      const response = await uploadDreamcastThumbnailFile(
                        file,
                        {
                          destination: "medias",
                        },
                      );
                      if (response?.data?.thumbnailUrl) {
                        setThumbnail({
                          src: response.data.thumbnailUrl,
                          alt: file.name || "Thumbnail",
                        });
                        setThumbnailTempFilename(
                          response.data.tempFilename || null,
                        );
                      }
                    } catch {
                      // Keep local blob URL if CDN upload fails
                    } finally {
                      setThumbnailUploading(false);
                    }
                  }}
                  onEdit={() => {
                    setThumbnailSource("generated");
                    setThumbnailBuilderOpen(true);
                  }}
                  onDelete={() => {
                    setThumbnail(null);
                    setThumbnailSource("none");
                    setThumbnailTempFilename(null);
                    setThumbnailBuilderData(null);
                    setThumbnailBuilderOpen(false);
                  }}
                />
              )}

              {thumbnailBuilderOpen && (
                <ThumbnailBuilder
                  ref={thumbnailBuilderRef}
                  initialData={thumbnailBuilderDraft || thumbnailBuilderData}
                  onDraftChange={(nextData) => {
                    setThumbnailBuilderDraft(nextData);
                    setThumbnailBuilderData(nextData);
                  }}
                  onDelete={() => {
                    setThumbnail(null);
                    setThumbnailSource("none");
                    setThumbnailTempFilename(null);
                    setThumbnailBuilderData(null);
                    setThumbnailBuilderDraft(null);
                    setThumbnailBuilderOpen(false);
                  }}
                  onDetailsChange={(details) => {
                    setAudioDetails((current) => ({
                      ...current,
                      sku: details?.sku || current.sku,
                    }));
                  }}
                  syncedDetails={{
                    title: audioDetails.title || "",
                    creatorSpeaker: audioDetails.creatorSpeaker || "",
                    topic: audioDetails.topic || "",
                    sku: audioDetails.sku || "",
                    naCrNumber: audioDetails.naCrNumber || "",
                    anzCrNumber: audioDetails.anzCrNumber || "",
                  }}
                  className={styles.thumbnailBuilder}
                />
              )}

              <FileUploadSection
                showManagedFiles
                defaultFileType={defaultFileType}
                sectionRef={fileUploadSectionRef}
                required={fileUploadRequired}
                hasError={fileUploadRequired}
                initialFiles={files}
                initialArchivedFiles={archivedFiles}
                onUploadStart={() =>
                  setFilesUploading((current) => current + 1)
                }
                onUploadEnd={() =>
                  setFilesUploading((current) => Math.max(0, current - 1))
                }
                onFileTypeChange={(fileType) => {
                  // When File Type changes, update Media Type to match
                  if (fileType === "Video") {
                    setAudioDetails((current) => ({
                      ...current,
                      mediaType: "Video",
                    }));
                  } else if (fileType === "Audio") {
                    // Audio file type stays as Audio or can also be Music
                    setAudioDetails((current) => ({
                      ...current,
                      mediaType: "Audio",
                    }));
                  }
                }}
                onArchivedFileRemove={(file) =>
                  setRemovedArchivedFiles((current) => {
                    if (!file?.id) {
                      return current;
                    }

                    const exists = current.some((item) => item?.id === file.id);
                    return exists ? current : [...current, file];
                  })
                }
                onFilesChange={({
                  files: nextFiles,
                  archivedFiles: nextArchived,
                }) => {
                  if (
                    loadingAudioId != null &&
                    Array.isArray(nextFiles) &&
                    Array.isArray(nextArchived) &&
                    nextFiles.length === 0 &&
                    nextArchived.length === 0
                  ) {
                    return;
                  }

                  filesRef.current = nextFiles;
                  archivedFilesRef.current = nextArchived;
                  setFiles(nextFiles);
                  setArchivedFiles(nextArchived);
                  setFileUploadRequired((current) => {
                    if (Array.isArray(nextFiles) && nextFiles.length > 0) {
                      return false;
                    }
                    return current;
                  });

                  // Sync CR numbers from active file back to audioDetails
                  if (Array.isArray(nextFiles) && nextFiles.length > 0) {
                    const activeFile = nextFiles[0];
                    if (
                      activeFile?.naCrNumber !== undefined ||
                      activeFile?.anzCrNumber !== undefined
                    ) {
                      setAudioDetails((current) => ({
                        ...current,
                        naCrNumber: activeFile.naCrNumber ?? current.naCrNumber,
                        anzCrNumber:
                          activeFile.anzCrNumber ?? current.anzCrNumber,
                      }));
                    }
                  }
                }}
              />
            </div>

            <div className={styles.rightColumn}>
              <PublishCard
                status={(() => {
                  const s = publishState.status || "";
                  // Map snake-case status to PascalCase for PropTypes
                  const statusMap = {
                    "in-review": "In Review",
                    "in review": "In Review",
                    archived: "Archived",
                    inreview: "InReview",
                  };
                  return statusMap[s.toLowerCase()] || s;
                })()}
                publishOn={publishState.publishOn}
                unpublishOn={
                  typeof publishState.unpublishOn === "string"
                    ? publishState.unpublishOn
                    : publishState.unpublishOn?.toISOString?.() ||
                      publishState.unpublishOn
                }
                showAsCurrentUntil={publishState.showAsCurrentUntil}
                unpublishEnabled={publishState.unpublishEnabled}
                digitalDeliveryEnabled={publishState.digitalDeliveryEnabled}
                showAsUpcomingOn={publishState.showAsUpcomingOn}
                collapseWhenNoStatus
                onChange={(nextPartial) =>
                  setPublishState((current) => ({ ...current, ...nextPartial }))
                }
              />

              <ContentSettingsCard
                membershipPermissions={
                  contentSettingsForRender.membershipPermissions
                }
                pinPermissions={contentSettingsForRender.pinPermissions}
                membershipGroupValuesById={
                  contentSettingsForRender.membershipGroupValuesById
                }
                membershipOptions={contentSettingsForRender.membershipOptions}
                pinLevels={contentSettingsForRender.pinLevels}
                familyReunionGroups={
                  contentSettingsForRender.familyReunionGroups
                }
                selectedFamilyGroups={
                  contentSettingsForRender.selectedFamilyGroups
                }
                diamonds={contentSettingsForRender.diamonds}
                selectedDiamonds={contentSettingsForRender.selectedDiamonds}
                additionalVisibilitySections={
                  contentSettingsForRender.additionalVisibilitySections
                }
                visibilitySelectionsByTypeCode={
                  contentSettingsForRender.visibilitySelectionsByTypeCode
                }
                familyReunionSectionTitle={
                  contentSettingsForRender.familyReunionSectionTitle
                }
                diamondsSectionTitle={
                  contentSettingsForRender.diamondsSectionTitle
                }
                additionalIbos={contentSettingsForRender.additionalIbos}
                tags={contentSettingsForRender.tags}
                onChange={setContentSettings}
                onTagCreated={handleTagCreated}
              />

              <MediaDisclosuresSection
                assignments={mediaDisclosures}
                disclosures={publishedDisclosures}
                mediaType={audioDetails.mediaType}
                onChange={setMediaDisclosures}
              />

              <AssignSpeakersSection
                speakers={speakers}
                onAdd={handleSpeakerAdd}
                addDisabled={isUploadInProgress}
                addDisabledReason="Please wait for the upload to finish before adding speakers."
                onRemove={(id) =>
                  setSpeakers((current) =>
                    current.filter((speaker) => speaker.id !== id),
                  )
                }
                onReorder={setSpeakers}
                onSpeakerVariationsChange={(
                  speakerId,
                  nextSelectedVariationIds,
                ) =>
                  setSpeakers((current) =>
                    current.map((speaker) =>
                      String(speaker.id) === String(speakerId)
                        ? {
                            ...speaker,
                            selectedVariationIds: Array.isArray(
                              nextSelectedVariationIds,
                            )
                              ? nextSelectedVariationIds
                              : [],
                          }
                        : speaker,
                    ),
                  )
                }
                onSearch={() => {}}
              />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default Media;
